import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { createPortal } from "react-dom";
import axios from "axios";
import { API, useAuth, getCookie } from "../App";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Search,
  Plus,
  Trash2,
  Loader2,
  Upload,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Save,
  X,
  Edit2,
  Check,
  Package,
  Keyboard,
  RotateCcw,
  ArrowRight,
  FileText,
  AlertCircle,
  CreditCard,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "./utils";
import CustomTooltip from "@/components/ui/CustomTooltip";
import { getOS } from "../hooks/useKeyboard";

const PACK_TYPES = ["Strip", "Bottle", "Tube", "Packet", "Box", "Unit"];

// [Issue #Suppliers] Premium Searchable & Infinite Scroll Dropdown
const SupplierSelector = ({
  selectedId,
  onSelect,
  knownSuppliers = [],
  placeholder = "Select Supplier",
  showAllOption = false,
  className = "",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const fetchSuppliers = async (p = 1, s = "", append = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/suppliers?page=${p}&limit=20&search=${encodeURIComponent(s)}`
      );
      const data = response.data.suppliers || [];
      setSuppliers((prev) => (append ? [...prev, ...data] : data));
      setHasMore(data.length === 20);
      setPage(p);
    } catch (err) {
      console.error("Failed to fetch suppliers", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && suppliers.length === 0) {
      fetchSuppliers(1, search, false);
    }
    if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  useEffect(() => {
    const delay = setTimeout(() => {
      if (isOpen) {
        fetchSuppliers(1, search, false);
        setHighlightedIndex(-1);
      }
    }, 300);
    return () => clearTimeout(delay);
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
      }
      return;
    }

    const maxIndex = suppliers.length - 1;
    const minIndex = showAllOption ? -1 : 0;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > minIndex ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex === -1 && showAllOption) {
          onSelect({ id: "all", name: "All Suppliers" });
          setIsOpen(false);
        } else if (
          highlightedIndex >= 0 &&
          highlightedIndex < suppliers.length
        ) {
          onSelect(suppliers[highlightedIndex]);
          setIsOpen(false);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      case "Tab":
        setIsOpen(false);
        break;
    }
  };

  // Auto-scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= -1 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const highlightedElement = container.querySelector(
        `[data-index="${highlightedIndex}"]`
      );
      if (highlightedElement) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = highlightedElement.getBoundingClientRect();

        if (elementRect.bottom > containerRect.bottom) {
          container.scrollTop += elementRect.bottom - containerRect.bottom;
        } else if (elementRect.top < containerRect.top) {
          container.scrollTop -= containerRect.top - elementRect.top;
        }
      }
    }
  }, [highlightedIndex]);

  // Combine local dropdown list with known suppliers from parent
  const allSuppliers = [...suppliers];
  if (Array.isArray(knownSuppliers)) {
    knownSuppliers.forEach((ks) => {
      if (ks && ks.id && !allSuppliers.find((s) => s.id === ks.id)) {
        allSuppliers.push(ks);
      }
    });
  }

  const selectedSupplier = allSuppliers.find((s) => s.id === selectedId);

  return (
    <div
      className={`relative ${className}`}
      ref={dropdownRef}
      onKeyDown={handleKeyDown}
    >
      <div
        className={`flex h-10 w-full items-center justify-between rounded-xl border bg-background/50 backdrop-blur-sm px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer hover:border-primary/50 transition-all ${isOpen ? "border-primary ring-2 ring-primary/10" : "border-border/70"}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        tabIndex={0}
      >
        <span
          className={`truncate ${!selectedId || selectedId === "" ? "text-muted-foreground/60" : "font-semibold text-foreground"}`}
        >
          {selectedId === "all"
            ? "All Suppliers"
            : selectedSupplier?.name || placeholder}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen && (
        <div className="absolute z-[100] mt-2 max-h-60 w-full overflow-hidden rounded-xl border border-border/80 bg-card/95 backdrop-blur-xl text-popover-foreground shadow-2xl animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center border-b border-border/40 px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground/60 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Search suppliers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div
            ref={scrollContainerRef}
            className="max-h-[200px] overflow-y-auto p-1 custom-scrollbar"
            onScroll={(e) => {
              const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
              if (
                scrollHeight - scrollTop <= clientHeight + 50 &&
                hasMore &&
                !loading
              ) {
                fetchSuppliers(page + 1, search, true);
              }
            }}
          >
            {showAllOption && (
              <div
                data-index="-1"
                className={`relative flex w-full cursor-default select-none items-center rounded-lg py-2.5 pl-9 pr-2 text-sm outline-none transition-colors ${highlightedIndex === -1 ? "bg-primary/10 text-primary font-medium" : selectedId === "all" ? "bg-primary/20 text-primary font-bold" : "text-foreground/80 hover:bg-primary/5"}`}
                onClick={() => {
                  onSelect({ id: "all", name: "All Suppliers" });
                  setIsOpen(false);
                }}
                onMouseEnter={() => setHighlightedIndex(-1)}
              >
                {selectedId === "all" && (
                  <Check className="absolute left-3 h-4 w-4 text-primary" />
                )}
                All Suppliers
              </div>
            )}
            {suppliers.map((s, idx) => (
              <div
                key={s.id}
                data-index={idx}
                className={`relative flex w-full cursor-default select-none items-center rounded-lg py-2 pl-9 pr-2 text-sm outline-none transition-colors ${highlightedIndex === idx ? "bg-primary/10 text-primary font-medium" : selectedId === s.id ? "bg-primary/20 text-primary font-bold" : "text-foreground/80 hover:bg-primary/5"}`}
                onClick={() => {
                  onSelect(s);
                  setIsOpen(false);
                }}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {selectedId === s.id && (
                  <Check className="absolute left-3 h-4 w-4 text-primary" />
                )}
                <div className="flex flex-col overflow-hidden">
                  <span className="truncate">{s.name}</span>
                  {s.phone && (
                    <span className="text-[10px] opacity-60 font-mono">
                      {s.phone}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="p-4 text-center">
                <Loader2 className="h-5 w-5 animate-spin text-primary mx-auto" />
              </div>
            )}
            {!loading && suppliers.length === 0 && (
              <div className="p-8 text-center text-xs text-muted-foreground italic">
                No suppliers match "{search}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function PurchasesPage() {
  // Data state
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [medicineSuggestions, setMedicineSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);

  // New purchase state - inline table approach
  const [showNewPurchase, setShowNewPurchase] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [purchaseItems, setPurchaseItems] = useState([]);
  const [newItemRow, setNewItemRow] = useState(null);
  const [searchMedicine, setSearchMedicine] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMouseOverSuggestions, setIsMouseOverSuggestions] = useState(false);
  const [aiAutofillEnabled, setAiAutofillEnabled] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);

  // Payment Tracking State
  const [paymentStatus, setPaymentStatus] = useState("Unpaid");
  const [amountPaid, setAmountPaid] = useState("");

  // Infinite scroll suggestion state
  const [suggestionPage, setSuggestionPage] = useState(1);
  const [hasMoreSuggestions, setHasMoreSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const [applyCgstToAll, setApplyCgstToAll] = useState(false);
  const [applySgstToAll, setApplySgstToAll] = useState(false);

  const applyCgstToAllRef = useRef(applyCgstToAll);
  const applySgstToAllRef = useRef(applySgstToAll);

  useEffect(() => {
    applyCgstToAllRef.current = applyCgstToAll;
  }, [applyCgstToAll]);

  useEffect(() => {
    applySgstToAllRef.current = applySgstToAll;
  }, [applySgstToAll]);

  const handleApplyCgstToAllChange = (checked) => {
    setApplyCgstToAll(checked);
    if (checked) {
      setPurchaseItems((prevItems) => {
        if (prevItems.length === 0) return prevItems;
        const firstRowCgst = prevItems[0].cgst;
        const cgstVal = parseFloat(firstRowCgst) || 0;
        return prevItems.map((item) => {
          const updated = { ...item, cgst: firstRowCgst };
          const qty = parseInt(updated.quantity || updated.pack_quantity) || 1;
          const ratePack =
            parseFloat(updated.rate_pack || updated.pack_price) || 0;
          const sgstVal = parseFloat(updated.sgst) || 0;
          const base = qty * ratePack;
          updated.total_amount = (
            base *
            (1 + (cgstVal + sgstVal) / 100)
          ).toFixed(2);
          return updated;
        });
      });
    }
  };

  const handleApplySgstToAllChange = (checked) => {
    setApplySgstToAll(checked);
    if (checked) {
      setPurchaseItems((prevItems) => {
        if (prevItems.length === 0) return prevItems;
        const firstRowSgst = prevItems[0].sgst;
        const sgstVal = parseFloat(firstRowSgst) || 0;
        return prevItems.map((item) => {
          const updated = { ...item, sgst: firstRowSgst };
          const qty = parseInt(updated.quantity || updated.pack_quantity) || 1;
          const ratePack =
            parseFloat(updated.rate_pack || updated.pack_price) || 0;
          const cgstVal = parseFloat(updated.cgst) || 0;
          const base = qty * ratePack;
          updated.total_amount = (
            base *
            (1 + (cgstVal + sgstVal) / 100)
          ).toFixed(2);
          return updated;
        });
      });
    }
  };

  // Edit existing purchase
  const [editingPurchase, setEditingPurchase] = useState(null);

  // Expanded view
  const [expandedPurchase, setExpandedPurchase] = useState(null);

  // Add Partial Payment dialog
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    purchase: null,
  });
  const [newPaymentAmount, setNewPaymentAmount] = useState("");
  const [newPaymentNotes, setNewPaymentNotes] = useState("");
  const [pdfConfirmDialog, setPdfConfirmDialog] = useState({
    open: false,
    purchaseId: null,
  });
  const [removeConfirmDialog, setRemoveConfirmDialog] = useState({
    open: false,
    itemId: null,
  });

  // Your existing state and refs are correct
  // Replace your current dropdownPosition state and updateDropdownPosition with this:
  // Track which item's dropdown is active
  const [activeItemId, setActiveItemId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    transform: "none",
  });

  const setInputRef = (el, itemId) => {
    if (el) {
      inputRefs.current[itemId] = el;
    }
  };

  const inputRefs = useRef({});

  const updateDropdownPosition = (itemId) => {
    const inputElement = inputRefs.current[itemId];
    if (!inputElement) return;

    const rect = inputElement.getBoundingClientRect();
    const dropdownWidth = Math.max(rect.width, 480);

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownMaxHeight = 300; // Purchases Page max height is 300px
    const positionAbove =
      spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      top: positionAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: dropdownWidth,
      transform: positionAbove ? "translateY(-100%)" : "none",
    });
  };

  // Bulletproof positioning sync
  useEffect(() => {
    if (!activeItemId || !showSuggestions) return;

    let rafId;
    const updatePosition = () => {
      updateDropdownPosition(activeItemId);
      rafId = requestAnimationFrame(updatePosition);
    };

    rafId = requestAnimationFrame(updatePosition);

    return () => cancelAnimationFrame(rafId);
  }, [activeItemId, showSuggestions]);
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    purchase: null,
  });

  // Keyboard shortcuts dialog
  const [showShortcuts, setShowShortcuts] = useState(false);

  //show salt dialog
  const [saltDialog, setSaltDialog] = useState({
    open: false,
    itemId: null,
    value: "",
  });

  // Refs for keyboard navigation
  const productInputRef = useRef(null);

  // State for dropdown keyboard navigation
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] =
    useState(-1);

  // Price history comparison state
  const [priceAlerts, setPriceAlerts] = useState({}); // { itemId: { productName, currentPrice, cheaperOptions, ... } }
  const [priceHistoryDialog, setPriceHistoryDialog] = useState({
    open: false,
    itemId: null,
    data: null,
  });

  // CSV Import State
  const [csvDialog, setCsvDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvColumns, setCsvColumns] = useState([]);
  const [csvSampleData, setCsvSampleData] = useState([]);
  const [csvMapping, setCsvMapping] = useState({
    product_name_col: "",
    batch_no_col: "",
    expiry_date_col: "",
    quantity_col: "",
    purchase_price_col: "",
    mrp_col: "",
    hsn_col: "",
  });

  // Pagination and Filter State
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 1,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isInitialMount = useRef(true);
  const [filterSupplier, setFilterSupplier] = useState(
    searchParams.get("supplier_id") || "all"
  );

  const handleSupplierFilterChange = (val) => {
    setFilterSupplier(val);
    if (val === "all") searchParams.delete("supplier_id");
    else searchParams.set("supplier_id", val);
    setSearchParams(searchParams);
  };
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("purchase_date");
  const [sortOrder, setSortOrder] = useState("desc");

  const [editingPurchaseId, setEditingPurchaseId] = useState(null);

  // Empty item template with enhanced fields
  const emptyItem = {
    id: "",
    product_id: "",
    product_name: "",
    manufacturer: "",
    salt_composition: "",
    pack_type: "Strip",
    batch_no: "",
    hsn_no: "",
    expiry_date: "",
    quantity: "1", // Number of packs (renamed from pack_quantity)
    units: "1", // Units per pack (renamed from units_per_pack)
    rate_pack: "", // Rate per pack (purchase price per pack)
    total_amount: "", // Total amount (user can enter this OR rate_pack)
    mrp_pack: "", // MRP per pack (user enters this, MRP/Unit is auto-calculated)
    cgst: "", // CGST (%)
    sgst: "", // SGST (%)
    _is_auto_filled_rate: true, // Prevent price history ping until manually edited
  };

  // ============ MULTI-TAB DRAFTS ============

  useEffect(() => {
    if (!user?.id) return;
    const STORAGE_KEY = `pharmalogy_purchase_drafts_${user.id}`;
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setTabs(parsed);
          const active = parsed[0];
          setActiveTabId(active.id);
          setSelectedSupplier(active.data.selectedSupplier || "");
          setInvoiceNo(active.data.invoiceNo || "");
          setPurchaseDate(
            active.data.purchaseDate || new Date().toISOString().slice(0, 10)
          );
          setPurchaseItems(active.data.purchaseItems || []);
          setPaymentStatus(active.data.paymentStatus || "Unpaid");
          setAmountPaid(active.data.amountPaid || "");
        }
      } catch (e) {
        console.error("Failed to parse purchase drafts", e);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !activeTabId) return;
    const delay = setTimeout(() => {
      setTabs((prev) => {
        const newTabs = prev.map((t) =>
          t.id === activeTabId
            ? {
                ...t,
                data: {
                  selectedSupplier,
                  invoiceNo,
                  purchaseDate,
                  purchaseItems,
                  paymentStatus,
                  amountPaid,
                },
              }
            : t
        );
        localStorage.setItem(
          `pharmalogy_purchase_drafts_${user.id}`,
          JSON.stringify(newTabs)
        );
        return newTabs;
      });
    }, 500);
    return () => clearTimeout(delay);
  }, [
    selectedSupplier,
    invoiceNo,
    purchaseDate,
    purchaseItems,
    paymentStatus,
    amountPaid,
    activeTabId,
    user?.id,
  ]);

  // Ensure at least one item row is present when creating/viewing a new purchase
  useEffect(() => {
    if (showNewPurchase && purchaseItems.length === 0) {
      const defaultRow = { ...emptyItem, id: `temp-${Date.now()}` };
      setPurchaseItems([defaultRow]);
      setTimeout(() => productInputRef.current?.focus(), 150);
    }
  }, [showNewPurchase, purchaseItems.length]);

  const createNewTab = () => {
    if (tabs.length >= 10) {
      toast.error("Maximum 10 tabs allowed");
      return;
    }
    const newId = uuidv4();
    const defaultRow = { ...emptyItem, id: `temp-${Date.now()}` };
    const newTab = {
      id: newId,
      data: {
        selectedSupplier: "",
        invoiceNo: "",
        purchaseDate: new Date().toISOString().slice(0, 10),
        purchaseItems: [defaultRow],
        paymentStatus: "Unpaid",
        amountPaid: "",
      },
    };
    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(newId);
    setSelectedSupplier("");
    setInvoiceNo("");
    setPurchaseDate(new Date().toISOString().slice(0, 10));
    setPurchaseItems([defaultRow]);
    setPaymentStatus("Unpaid");
    setAmountPaid("");
    setShowNewPurchase(true);
    setTimeout(() => productInputRef.current?.focus(), 150);
  };

  const switchTab = (tabId) => {
    const target = tabs.find((t) => t.id === tabId);
    if (!target) return;

    // Save current active tab specifically before switching if needed (already handled by effect, but let's ensure states are swapped)
    setActiveTabId(tabId);
    setSelectedSupplier(target.data.selectedSupplier || "");
    setInvoiceNo(target.data.invoiceNo || "");
    setPurchaseDate(
      target.data.purchaseDate || new Date().toISOString().slice(0, 10)
    );
    setPurchaseItems(target.data.purchaseItems || []);
    setPaymentStatus(target.data.paymentStatus || "Unpaid");
    setAmountPaid(target.data.amountPaid || "");
    setShowNewPurchase(true);
  };

  const closeTab = (tabId) => {
    const isClosingActive = tabId === activeTabId;
    const remainingTabs = tabs.filter((t) => t.id !== tabId);

    setTabs(remainingTabs);

    if (remainingTabs.length > 0) {
      if (isClosingActive) {
        const next = remainingTabs[0];
        switchTab(next.id);
      }
    } else {
      setActiveTabId(null);
      setSelectedSupplier("");
      setInvoiceNo("");
      setPurchaseDate(new Date().toISOString().slice(0, 10));
      setPurchaseItems([]);
      setPaymentStatus("Unpaid");
      setAmountPaid("");
      setShowNewPurchase(false);
    }

    if (user?.id) {
      localStorage.setItem(
        `pharmalogy_purchase_drafts_${user.id}`,
        JSON.stringify(remainingTabs)
      );
    }
  };

  const clearDraft = () => {
    if (activeTabId) closeTab(activeTabId);
  };

  // Refs for keyboard shortcuts to avoid stale closures
  const handlersRef = useRef({});

  // Keep refs updated
  useEffect(() => {
    handlersRef.current = {
      handleStartNewPurchase,
      handleSubmitPurchase,
      handleSaveEditPurchase,
      handleCancelAddItem,
      handleCancelNewPurchase,
      handleAddNewRow,
      setShowShortcuts,
      showNewPurchase,
      newItemRow,
      editingPurchaseId,
    };
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isInput = ["INPUT", "TEXTAREA", "SELECT"].includes(
        e.target?.tagName
      );
      if (
        isInput &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        e.key !== "Escape" &&
        e.key !== "Enter"
      )
        return;

      const {
        handleStartNewPurchase,
        handleSubmitPurchase,
        handleSaveEditPurchase,
        handleCancelAddItem,
        handleCancelNewPurchase,
        handleAddNewRow,
        setShowShortcuts,
        showNewPurchase,
        newItemRow,
        editingPurchaseId,
      } = handlersRef.current;

      if (!handleStartNewPurchase) return;

      // Alt+N - New purchase
      if (e.altKey && (e.key === "n" || e.code === "KeyN")) {
        e.preventDefault();
        handleStartNewPurchase();
      }
      // Alt+S or Cmd+Enter - Save purchase
      if (
        (e.altKey && (e.key === "s" || e.code === "KeyS")) ||
        ((e.ctrlKey || e.metaKey) && (e.key === "Enter" || e.code === "Enter"))
      ) {
        if (showNewPurchase) {
          e.preventDefault();
          handleSubmitPurchase();
        } else if (editingPurchaseId) {
          e.preventDefault();
          handleSaveEditPurchase();
        }
      }
      // Escape - Cancel
      if (e.key === "Escape" || e.code === "Escape") {
        if (newItemRow) {
          handleCancelAddItem();
        } else if (showNewPurchase) {
          handleCancelNewPurchase();
        }
      }
      // Alt+A - Add item row
      if (e.altKey && (e.key === "a" || e.code === "KeyA")) {
        if (showNewPurchase) {
          e.preventDefault();
          handleAddNewRow();
        }
      }
      // Alt+? - Show shortcuts
      if (e.altKey && (e.key === "/" || e.code === "Slash")) {
        e.preventDefault();
        setShowShortcuts(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const fetchPurchases = useCallback(
    async (page = 1, highlightId = undefined) => {
      try {
        const params = new URLSearchParams();
        if (!highlightId) {
          params.append("page", page);
        }
        params.append("limit", pagination.limit);
        params.append("sort_by", sortBy);
        params.append("sort_order", sortOrder);
        if (searchQuery) params.append("search", searchQuery);
        if (filterSupplier && filterSupplier !== "all")
          params.append("supplier_id", filterSupplier);
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        if (highlightId) params.append("highlight_id", highlightId);

        const response = await axios.get(
          `${API}/purchases?${params.toString()}`
        );
        setPurchases(response.data.purchases);
        setPagination(response.data.pagination);

        if (highlightId) {
          setTimeout(() => {
            setExpandedPurchase(highlightId);
            const el = document.getElementById(`record-${highlightId}`);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              el.classList.add("bg-primary/20", "transition-all", "duration-1000");
              setTimeout(() => {
                el.classList.remove("bg-primary/20");
              }, 3000);
            }
            window.history.replaceState({}, document.title);
          }, 300);
        }
      } catch (error) {
        toast.error("Failed to load purchases");
      }
    },
    [
      searchQuery,
      filterSupplier,
      startDate,
      endDate,
      sortBy,
      sortOrder,
      pagination.limit,
    ]
  );

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (location.state?.highlightId) {
        return;
      }
    }
    const debounce = setTimeout(() => {
      fetchPurchases(1);
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, filterSupplier, startDate, endDate, sortBy, sortOrder]);

  // Search medicines for suggestions with better ranking
  useEffect(() => {
    const searchMedicines = async () => {
      if (searchMedicine.length >= 2) {
        setLoadingSuggestions(true);
        try {
          const response = await axios.get(
            `${API}/medicines/search?q=${encodeURIComponent(searchMedicine)}&limit=20&fuzzy=true&page=1`
          );

          setMedicineSuggestions(response.data.medicines || []);
          setHasMoreSuggestions(response.data.meta?.has_more || false);
          setSuggestionPage(1);
          setShowSuggestions(true);
          setHighlightedSuggestionIndex(-1);
        } catch (error) {
          console.error("Medicine search error:", error);
        } finally {
          setLoadingSuggestions(false);
        }
      } else {
        setMedicineSuggestions([]);
        setShowSuggestions(false);
        setHighlightedSuggestionIndex(-1);
        setHasMoreSuggestions(false);
        setSuggestionPage(1);
      }
    };

    const debounce = setTimeout(searchMedicines, 300);
    return () => clearTimeout(debounce);
  }, [searchMedicine]);

  const loadMoreSuggestions = async () => {
    if (loadingSuggestions || !hasMoreSuggestions) return;

    setLoadingSuggestions(true);
    try {
      const nextPage = suggestionPage + 1;
      const response = await axios.get(
        `${API}/medicines/search?q=${encodeURIComponent(searchMedicine)}&limit=20&fuzzy=true&page=${nextPage}`
      );

      setMedicineSuggestions((prev) => [
        ...prev,
        ...(response.data.medicines || []),
      ]);
      setHasMoreSuggestions(response.data.meta?.has_more || false);
      setSuggestionPage(nextPage);
    } catch (error) {
      console.error("Load more medicines error:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleScrollSuggestions = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight < 50;
    if (bottom) {
      loadMoreSuggestions();
    }
  };

  const fetchData = async () => {
    try {
      const initialSuppliers = [];
      const supplierIdFromUrl = searchParams.get("supplier_id");

      if (supplierIdFromUrl && supplierIdFromUrl !== "all") {
        try {
          const res = await axios.get(`${API}/suppliers/${supplierIdFromUrl}`);
          if (res.data?.supplier) initialSuppliers.push(res.data.supplier);
        } catch (e) {
          console.error("Filter supplier fetch error", e);
        }
      }

      const suppliersRes = await axios.get(`${API}/suppliers?limit=50`);
      const fetched = suppliersRes.data.suppliers || [];

      const combined = [...initialSuppliers];
      fetched.forEach((f) => {
        if (!combined.find((c) => c.id === f.id)) combined.push(f);
      });

      setSuppliers(combined);
      const hlId = location.state?.highlightId;
      if (hlId) {
        await fetchPurchases(1, hlId);
      } else {
        await fetchPurchases(1);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  //Purchase pdf
  const handleGeneratePurchasePdf = async (purchaseId) => {
    try {
      const res = await axios.post(`${API}/purchases/${purchaseId}/pdf`);

      if (res.data?.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
        toast.success("PDF generated");
      } else {
        toast.error("PDF generation failed");
      }
    } catch (error) {
      toast.error("Failed to generate PDF");
    }
  };

  // ============ NEW PURCHASE - INLINE TABLE ============

  const handleStartNewPurchase = () => {
    if (!showNewPurchase && tabs.length === 0) {
      createNewTab();
    } else {
      setShowNewPurchase(true);
      setTimeout(() => {
        if (productInputRef.current) {
          productInputRef.current.focus();
        } else {
          document.getElementById("search-medicine-input")?.focus();
        }
      }, 150);
    }
  };

  const handleCancelNewPurchase = () => {
    setShowNewPurchase(false);
    setEditingPurchaseId(null);
    setPurchaseItems([]);
    setSelectedSupplier("");
    setInvoiceNo("");
    setPurchaseDate(new Date().toISOString().slice(0, 10));
    setNewItemRow(null);
    setShowSuggestions(false);
    setApplyCgstToAll(false);
    setApplySgstToAll(false);
    clearDraft();
  };

  const handleStartAddItem = () => {
    setNewItemRow({ ...emptyItem, id: `temp-${Date.now()}` });
    setSearchMedicine("");
    setShowSuggestions(false);
    // Focus product input after render
    setTimeout(() => productInputRef.current?.focus(), 100);
  };

  const handleCancelAddItem = () => {
    setNewItemRow(null);
    setSearchMedicine("");
    setMedicineSuggestions([]);
    setShowSuggestions(false);
  };

  // Add new empty row to items
  const handleAddNewRow = () => {
    setPurchaseItems((prev) => {
      let cgst = "";
      let sgst = "";
      if (prev.length > 0) {
        if (applyCgstToAllRef.current) {
          cgst = prev[0].cgst;
        }
        if (applySgstToAllRef.current) {
          sgst = prev[0].sgst;
        }
      }
      const newRow = { ...emptyItem, id: `temp-${Date.now()}`, cgst, sgst };
      return [...prev, newRow];
    });
    // Focus the last row's product input after render
    setTimeout(() => productInputRef.current?.focus(), 100);
  };

  const handleNewItemChange = (field, value) => {
    setNewItemRow((prev) => {
      const updated = { ...prev, [field]: value };

      // Bidirectional calculation: Rate per Pack ↔ Total Amount
      const qty = parseInt(updated.quantity) || 1;

      if (field === "rate_pack" && value !== "") {
        // User entered rate_pack, calculate total_amount
        const ratePack = parseFloat(value) || 0;
        updated.total_amount = (qty * ratePack).toFixed(2);
      } else if (field === "total_amount" && value !== "") {
        // User entered total_amount, calculate rate_pack
        const totalAmt = parseFloat(value) || 0;
        updated.rate_pack = qty > 0 ? (totalAmt / qty).toFixed(2) : "0";
      } else if (field === "quantity" && value !== "") {
        // Quantity changed, recalculate based on which field has value
        if (updated.rate_pack && updated.rate_pack !== "") {
          const ratePack = parseFloat(updated.rate_pack) || 0;
          updated.total_amount = (qty * ratePack).toFixed(2);
        } else if (updated.total_amount && updated.total_amount !== "") {
          const totalAmt = parseFloat(updated.total_amount) || 0;
          updated.rate_pack = qty > 0 ? (totalAmt / qty).toFixed(2) : "0";
        }
      }

      return updated;
    });
    if (field === "product_name") {
      setSearchMedicine(value);
    }
  };

  const handleSelectMedicine = (medicine) => {
    const pricePerUnit = parseFloat(medicine["price(₹)"]) || 0;
    // Extract pack size info if available
    const packSizeMatch = medicine.pack_size?.match(/(\d+)/);
    const unitsPerPack = packSizeMatch ? parseInt(packSizeMatch[1]) : 1;

    // Calculate MRP per pack from MRP per unit
    const mrpPack = pricePerUnit * unitsPerPack;
    // Estimated purchase price per pack (70% of MRP)
    const ratePack = mrpPack * 0.7;

    setNewItemRow((prev) => ({
      ...prev,
      product_id: medicine.id || `med-${Date.now()}`,
      product_name: medicine.name,
      manufacturer: medicine.manufacturer || "",
      salt_composition: medicine.composition || "",
      units: String(unitsPerPack),
      rate_pack: ratePack.toFixed(2),
      mrp_pack: mrpPack.toFixed(2), // Now storing MRP per pack
    }));
    setSearchMedicine("");
    setMedicineSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSaveNewItem = () => {
    // Only product_name, quantity, and rate_pack are mandatory
    if (!newItemRow.product_name) {
      toast.error("Please enter product name");
      return;
    }
    if (!newItemRow.quantity || !newItemRow.rate_pack) {
      toast.error("Please enter quantity and rate per pack");
      return;
    }

    const qty = parseInt(newItemRow.quantity) || 1;
    const units = parseInt(newItemRow.units) || 1;
    const ratePack = parseFloat(newItemRow.rate_pack) || 0;
    const mrpPack = parseFloat(newItemRow.mrp_pack) || 0;

    const totalUnits = qty * units;
    const rateUnit = units > 0 ? ratePack / units : ratePack;
    const mrpUnit = units > 0 ? mrpPack / units : mrpPack;
    const finalAmount = qty * ratePack;

    const item = {
      ...newItemRow,
      product_id: newItemRow.product_id || `temp-${Date.now()}`,
      // For API compatibility, map to expected field names
      pack_quantity: qty,
      units_per_pack: units,
      pack_price: ratePack,
      mrp_per_unit: mrpUnit,
      // Display fields
      quantity: qty,
      units: units,
      rate_pack: ratePack,
      rate_unit: rateUnit,
      mrp_unit: mrpUnit,
      mrp_pack: mrpPack,
      total_units: totalUnits,
      final_amount: finalAmount,
    };

    setPurchaseItems((prev) => [...prev, item]);
    setNewItemRow(null);
    setSearchMedicine("");
    setMedicineSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle Tab key for default values
  const handleTabDefault = (e, field) => {
    if (e.key === "Tab" && !e.shiftKey) {
      const value = e.target.value;
      if (!value || value === "") {
        e.preventDefault();
        let defaultValue = "";
        switch (field) {
          case "quantity":
          case "units":
            defaultValue = "1";
            break;
          case "rate_pack":
          case "mrp_pack":
            defaultValue = "0";
            break;
          case "pack_type":
            defaultValue = "Strip";
            break;
          case "batch_no":
            defaultValue = `B${Date.now().toString().slice(-6)}`;
            break;
          default:
            // Move to next field without default
            return;
        }
        handleNewItemChange(field, defaultValue);
        // Move to next focusable element
        const form = e.target.form || e.target.closest("tr");
        const inputs = form?.querySelectorAll("input, select, button");
        const currentIndex = Array.from(inputs || []).indexOf(e.target);
        if (inputs && currentIndex >= 0 && currentIndex < inputs.length - 1) {
          setTimeout(() => inputs[currentIndex + 1]?.focus(), 0);
        }
      }
    }
  };

  // Handle keyboard events in item row
  const handleItemKeyDown = (e, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveNewItem();
    } else if (e.key === "Escape") {
      handleCancelAddItem();
    } else if (e.key === "Tab") {
      handleTabDefault(e, field);
    }
  };

  const handleRemoveItem = (itemId) => {
    setRemoveConfirmDialog({ open: true, itemId });
  };

  const confirmRemoveItem = () => {
    if (!removeConfirmDialog.itemId) return;
    setPurchaseItems((prev) =>
      prev.filter((item) => item.id !== removeConfirmDialog.itemId)
    );
    setRemoveConfirmDialog({ open: false, itemId: null });
  };

  const handleEditItem = (item) => {
    setPurchaseItems((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, _editing: true } : i))
    );
  };

  const handleSaveEditedItem = (itemId) => {
    setPurchaseItems((prev) =>
      prev.map((i) => {
        if (i.id === itemId) {
          const { _editing, ...rest } = i;
          const qty =
            parseInt(rest.quantity) || parseInt(rest.pack_quantity) || 1;
          const units =
            parseInt(rest.units) || parseInt(rest.units_per_pack) || 1;
          const ratePack =
            parseFloat(rest.rate_pack) || parseFloat(rest.pack_price) || 0;
          const mrpUnit =
            parseFloat(rest.mrp_unit) || parseFloat(rest.mrp_per_unit) || 0;

          return {
            ...rest,
            pack_quantity: qty,
            units_per_pack: units,
            pack_price: ratePack,
            mrp_per_unit: mrpUnit,
            quantity: qty,
            units: units,
            rate_pack: ratePack,
            rate_unit: units > 0 ? ratePack / units : ratePack,
            mrp_unit: mrpUnit,
            mrp_pack: mrpUnit * units,
            total_units: qty * units,
            final_amount: qty * ratePack,
          };
        }
        return i;
      })
    );
  };

  const handleCancelEditItem = (itemId) => {
    setPurchaseItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, _editing: false } : i))
    );
  };

  const handleItemFieldChange = (itemId, field, value) => {
    setPurchaseItems((prev) =>
      prev.map((i) => (i.id === itemId ? { ...i, [field]: value } : i))
    );

    // Update search for medicine suggestions
    if (field === "product_name") {
      setSearchMedicine(value);
    }
  };

  // Handle field change with bidirectional calculation for Rate/Total
  const handleItemFieldChangeWithCalc = (itemId, field, value) => {
    setPurchaseItems((prev) => {
      const isFirstRow = prev.length > 0 && prev[0].id === itemId;
      if (!isFirstRow) {
        if (field === "cgst" && applyCgstToAllRef.current) {
          setTimeout(() => setApplyCgstToAll(false), 0);
        }
        if (field === "sgst" && applySgstToAllRef.current) {
          setTimeout(() => setApplySgstToAll(false), 0);
        }
      }

      const shouldPropagateCgst =
        isFirstRow && field === "cgst" && applyCgstToAllRef.current;
      const shouldPropagateSgst =
        isFirstRow && field === "sgst" && applySgstToAllRef.current;

      return prev.map((item) => {
        const isTarget = item.id === itemId;
        if (!isTarget && !shouldPropagateCgst && !shouldPropagateSgst)
          return item;

        let updatedValue = isTarget ? value : item[field];
        if (field === "cgst" && shouldPropagateCgst) {
          updatedValue = value;
        }
        if (field === "sgst" && shouldPropagateSgst) {
          updatedValue = value;
        }

        const updated = { ...item, [field]: updatedValue };

        if (isTarget && (field === "rate_pack" || field === "total_amount")) {
          updated._is_auto_filled_rate = false;
        }

        const qty =
          parseInt(
            field === "quantity" && isTarget
              ? value
              : updated.quantity || updated.pack_quantity
          ) || 1;
        const ratePack =
          parseFloat(
            field === "rate_pack" && isTarget
              ? value
              : updated.rate_pack || updated.pack_price
          ) || 0;
        const cgstVal =
          parseFloat(
            field === "cgst" && (isTarget || shouldPropagateCgst)
              ? value
              : updated.cgst
          ) || 0;
        const sgstVal =
          parseFloat(
            field === "sgst" && (isTarget || shouldPropagateSgst)
              ? value
              : updated.sgst
          ) || 0;

        const base = qty * ratePack;

        if (
          (field === "cgst" && (isTarget || shouldPropagateCgst)) ||
          (field === "sgst" && (isTarget || shouldPropagateSgst)) ||
          (field === "quantity" && isTarget) ||
          (field === "rate_pack" && isTarget)
        ) {
          // Recalculate total_amount to include GST
          updated.total_amount = (
            base *
            (1 + (cgstVal + sgstVal) / 100)
          ).toFixed(2);
        } else if (field === "total_amount" && isTarget) {
          const newTotal = parseFloat(value) || 0;
          if (base > 0) {
            const totalTaxRate = ((newTotal - base) / base) * 100;
            const halfTax = Math.max(
              0,
              parseFloat((totalTaxRate / 2).toFixed(2))
            );
            updated.cgst = String(halfTax);
            updated.sgst = String(halfTax);
          } else {
            updated.rate_pack = qty > 0 ? (newTotal / qty).toFixed(2) : "0";
            updated.cgst = "0";
            updated.sgst = "0";
          }
        }

        return updated;
      });
    });

    // // Check price history when rate_pack is entered
    // if (field === "rate_pack" && value !== "") {
    //   const item = purchaseItems.find((i) => i.id === itemId);
    //   if (item?.product_name) {
    //     checkPriceHistory(itemId, item.product_name, parseFloat(value) || 0);
    //   }
    // }
  };
  // Optimize: Check price history silently (used onBlur and immediately on auto-fill)
  const checkPriceHistorySilent = async (
    itemId,
    productName,
    currentPriceParam
  ) => {
    const currentPrice = parseFloat(currentPriceParam);
    if (!productName || !currentPrice || currentPrice <= 0) return;

    try {
      const token = getCookie("pharmalogy_token");
      const response = await axios.get(`${API}/purchases/price-history`, {
        params: { product_name: productName, current_price: currentPrice },
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
      if (data.is_higher_than_history && data.cheaper_options?.length > 0) {
        setPriceAlerts((prev) => ({
          ...prev,
          [itemId]: data,
        }));
      } else {
        // Clear alert if it's fine now
        setPriceAlerts((prev) => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
      }
    } catch (error) {
      console.error("Silent Check Price History Error:", error);
    }
  };

  // Check historical prices for a product
  const checkPriceHistory = async (itemId, productName, currentPrice) => {
    if (!productName || currentPrice <= 0) {
      toast.error("Invalid product or price");
      return;
    }

    try {
      const token = getCookie("pharmalogy_token");

      const response = await axios.get(`${API}/purchases/price-history`, {
        params: { product_name: productName, current_price: currentPrice },
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;

      // CASE 1: Cheaper options exist
      if (data.is_higher_than_history && data.cheaper_options?.length > 0) {
        setPriceHistoryDialog({
          open: true,
          itemId,
          data: {
            type: "higher",
            searched_product_name: data.product_name,
            matched_product_name: data.matched_product_name, // if you added it
            currentPrice: data.current_price || 0,
            cheapestPrice: data.cheapest_historical_price || 0,
            priceDifference: data.price_difference || 0,
            cheaperOptions: data.cheaper_options || [],
          },
        });

        return;
      }

      // CASE 2: Current is cheapest
      if (
        data.cheapest_historical_price !== null &&
        currentPrice <= data.cheapest_historical_price
      ) {
        setPriceHistoryDialog({
          open: true,
          itemId,
          data: {
            type: "cheapest",
            ...data,
          },
        });
        return;
      }

      // CASE 3: No history
      setPriceHistoryDialog({
        open: true,
        itemId,
        data: {
          type: "no-history",
          ...data,
        },
      });
    } catch (error) {
      toast.error("Failed to compare prices");
    }
  };

  const buildSaltComposition = (medicine) => {
    return [medicine?.short_composition1, medicine?.short_composition2]
      .map((v) => v?.trim())
      .filter(Boolean)
      .join(", ");
  };

  // Handle selecting medicine from suggestions for an item row
  const handleSelectMedicineForItem = async (itemId, medicine) => {
    const isInventory = medicine.source === "inventory";

    // Extract pack size
    let unitsPerPack = 1;
    if (isInventory && medicine.pack_size) {
      const match = medicine.pack_size.match(/(\d+)/);
      unitsPerPack = match ? parseInt(match[1]) : 1;
    } else if (medicine.pack_size_label) {
      const match = medicine.pack_size_label.match(/(\d+)/);
      unitsPerPack = match ? parseInt(match[1]) : 1;
    }

    // Calculate prices
    const mrpPerUnit =
      parseFloat(
        medicine.mrp_per_unit || medicine.mrp || medicine["price(₹)"]
      ) || 0;
    const mrpPack = mrpPerUnit * unitsPerPack;

    // Use historical purchase price if inventory, else 70% of MRP
    const ratePerUnit =
      isInventory && medicine.purchase_price
        ? parseFloat(medicine.purchase_price)
        : mrpPerUnit * 0.7;
    const ratePack = ratePerUnit * unitsPerPack;

    let manufacturer =
      medicine.manufacturer || medicine.manufacturer_name || "";
    let salt_composition =
      medicine.salt_composition ||
      [medicine.short_composition1, medicine.short_composition2]
        .filter(Boolean)
        .join(", ") ||
      "";

    const ratePackFixed = ratePack.toFixed(2);

    setPurchaseItems((prev) => {
      const isFirstRow = prev.length > 0 && prev[0].id === itemId;
      const finalCgst =
        isInventory && medicine.cgst !== undefined ? String(medicine.cgst) : "";
      const finalSgst =
        isInventory && medicine.sgst !== undefined ? String(medicine.sgst) : "";

      const shouldPropagateCgst = isFirstRow && applyCgstToAllRef.current;
      const shouldPropagateSgst = isFirstRow && applySgstToAllRef.current;
      return prev.map((item) => {
        if (item.id !== itemId && !shouldPropagateCgst && !shouldPropagateSgst)
          return item;

        const qty =
          parseInt(
            item.id === itemId
              ? item.quantity
              : item.quantity || item.pack_quantity
          ) || 1;
        const currentRatePack =
          item.id === itemId
            ? ratePack
            : parseFloat(item.rate_pack || item.pack_price) || 0;

        let cgstStr = item.cgst;
        if (item.id === itemId) {
          cgstStr = finalCgst;
        } else if (shouldPropagateCgst) {
          cgstStr = finalCgst;
        }

        let sgstStr = item.sgst;
        if (item.id === itemId) {
          sgstStr = finalSgst;
        } else if (shouldPropagateSgst) {
          sgstStr = finalSgst;
        }

        const currentCgstVal = parseFloat(cgstStr) || 0;
        const currentSgstVal = parseFloat(sgstStr) || 0;

        const base = qty * currentRatePack;
        const total = (
          base *
          (1 + (currentCgstVal + currentSgstVal) / 100)
        ).toFixed(2);

        if (item.id === itemId) {
          return {
            ...item,
            product_id: medicine.id || `med-${Date.now()}`,
            product_name: medicine.product_name || medicine.name,
            manufacturer,
            salt_composition,
            units: String(unitsPerPack),
            rate_pack: ratePackFixed,
            mrp_pack: mrpPack.toFixed(2),
            total_amount: total,
            cgst: cgstStr,
            sgst: sgstStr,
            _is_auto_filled_rate: true,

            // Store inventory metadata for visual indicators
            _inventoryMeta: isInventory
              ? {
                  batch_no: medicine.batch_no,
                  expiry_date: medicine.expiry_date,
                  available_quantity: medicine.available_quantity,
                  stock_status: medicine.stock_status,
                  last_supplier: medicine.supplier_name,
                  match_quality: medicine.matchQuality, // 'exact', 'good', or 'fuzzy'
                }
              : null,
          };
        } else {
          // Propagated row updates
          const updated = { ...item };
          if (shouldPropagateCgst) {
            updated.cgst = cgstStr;
          }
          if (shouldPropagateSgst) {
            updated.sgst = sgstStr;
          }
          updated.total_amount = total;
          return updated;
        }
      });
    });

    // Call silent ping immediately so if the loaded price is high, it highlights!
    checkPriceHistorySilent(
      itemId,
      medicine.product_name || medicine.name,
      ratePackFixed
    );

    setSearchMedicine("");
    setMedicineSuggestions([]);
    setShowSuggestions(false);
    setIsMouseOverSuggestions(false);
    setActiveItemId(null);

    // Optional: Show toast for fuzzy matches
    if (isInventory && medicine.matchQuality === "fuzzy") {
      toast.info(`Fuzzy match: "${medicine.name}" - verify before saving`);
    }

    // AI Enrichment Trigger
    if (aiAutofillEnabled && (!manufacturer || !salt_composition)) {
      try {
        setIsAiLoading(true);
        const res = await axios.post(`${API}/medicines/enrich`, {
          product_name: medicine.product_name || medicine.name,
        });

        if (res.data.manufacturer || res.data.salt_composition) {
          setPurchaseItems((prev) =>
            prev.map((item) => {
              if (item.id !== itemId) return item;
              return {
                ...item,
                manufacturer: item.manufacturer || res.data.manufacturer,
                salt_composition:
                  item.salt_composition || res.data.salt_composition,
              };
            })
          );
          toast.success("AI Autofilled missing metadata ✨");
        }
      } catch (err) {
        console.warn("AI enrichment request failed or timed out.");
      } finally {
        setIsAiLoading(false);
      }
    }
  };

  // Direct AI resolution when no suggestions are found or user overrides
  const handleSelectAiMedicineForItem = async (itemId, productName) => {
    // 1. Flush local search state and inject base product
    setPurchaseItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          product_id: `ai-${Date.now()}`,
          product_name: productName,
          manufacturer: "",
          salt_composition: "",
          units: "1",
          rate_pack: "",
          mrp_pack: "",
          total_amount: "",
          _inventoryMeta: null,
        };
      })
    );

    setSearchMedicine("");
    setMedicineSuggestions([]);
    setShowSuggestions(false);
    setHighlightedSuggestionIndex(-1);

    // 2. Force authoritative AI fetch
    try {
      setIsAiLoading(true);
      const res = await axios.post(`${API}/medicines/enrich`, {
        product_name: productName,
      });

      if (res.data.manufacturer || res.data.salt_composition) {
        setPurchaseItems((prev) =>
          prev.map((item) => {
            if (item.id !== itemId) return item;
            return {
              ...item,
              manufacturer: res.data.manufacturer || item.manufacturer,
              salt_composition:
                res.data.salt_composition || item.salt_composition,
            };
          })
        );
        toast.success("AI recovered global product attributes ✨");
      } else {
        toast.info("AI couldn't confidently extract properties for this item.");
      }
    } catch (err) {
      console.warn("AI direct lookup failed", err);
      toast.error("AI lookup failed to connect.");
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleSubmitPurchase = async () => {
    if (!selectedSupplier) {
      toast.error("Please select a supplier");
      return;
    }

    const validItems = purchaseItems.filter(
      (item) => item.product_name && item.product_name.trim() !== ""
    );

    if (validItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    const supplier = suppliers.find((s) => s.id === selectedSupplier);

    setSubmitting(true);

    try {
      const payload = {
        supplier_id: selectedSupplier,
        supplier_name: supplier?.name || "Unknown",
        invoice_no: invoiceNo || null,
        purchase_date: purchaseDate,
        items: validItems.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          batch_no: item.batch_no || null,
          expiry_date: item.expiry_date || null,
          manufacturer: item.manufacturer || null,
          salt_composition: item.salt_composition || null,
          pack_type: item.pack_type || "Strip",
          pack_quantity: parseInt(item.quantity) || 1,
          units_per_pack: parseInt(item.units) || 1,
          pack_price: parseFloat(item.rate_pack) || 0,
          mrp_per_unit:
            (parseFloat(item.mrp_pack) || 0) / (parseInt(item.units) || 1),
          hsn_no: item.hsn_no || null,
          cgst: parseFloat(item.cgst) || 0,
          sgst: parseFloat(item.sgst) || 0,
        })),
        payment_status: paymentStatus,
        amount_paid:
          paymentStatus === "Partial"
            ? parseFloat(amountPaid) || 0
            : paymentStatus === "Paid"
              ? undefined /* backend handles total */
              : 0,
      };

      if (editingPurchaseId) {
        await axios.put(
          `${API}/purchases/${editingPurchaseId}?update_inventory=true`,
          payload
        );
        toast.success("Purchase updated successfully");
      } else {
        const res = await axios.post(`${API}/purchases`, payload);
        toast.success("Purchase recorded successfully");

        const purchaseId = res.data?.purchase?.id;

        if (purchaseId) {
          setPdfConfirmDialog({ open: true, purchaseId });
        }
      }

      setEditingPurchaseId(null);
      setPaymentStatus("Unpaid");
      setAmountPaid("");
      clearDraft();
      handleCancelNewPurchase();
      await fetchPurchases(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save purchase");
    } finally {
      setSubmitting(false);
    }
  };
  // ============ EDIT EXISTING PURCHASE ============

  // const handleStartEditPurchase = (purchase) => {
  //   setEditingPurchase({
  //     ...purchase,
  //     items: purchase.items.map((item, idx) => ({
  //       ...item,
  //       id: item.id || `existing-${idx}`,
  //       pack_quantity: item.pack_quantity || item.quantity || 1,
  //       units_per_pack: item.units_per_pack || 1,
  //       pack_price:
  //         item.pack_price ||
  //         item.purchase_price * (item.units_per_pack || 1) ||
  //         0,
  //       mrp_per_unit: item.mrp_per_unit || item.mrp || 0,
  //     })),
  //   });
  // };

  const handleStartEditPurchase = (purchase) => {
    setShowNewPurchase(true);
    setEditingPurchaseId(purchase.id);

    setSelectedSupplier(purchase.supplier_id);
    setInvoiceNo(purchase.invoice_no || "");
    setPurchaseDate(
      purchase.purchase_date ||
        purchase.created_at?.slice(0, 10) ||
        new Date().toISOString().slice(0, 10)
    );
    setPaymentStatus(purchase.payment_status || "Unpaid");
    setAmountPaid(purchase.amount_paid || "");

    const mappedItems = purchase.items.map((item, idx) => ({
      id: item.id || `edit-${idx}`,
      product_id: item.product_id,
      product_name: item.product_name,
      manufacturer: item.manufacturer || "",
      salt_composition: item.salt_composition || "",
      pack_type: item.pack_type || "Strip",
      batch_no: item.batch_no || "",
      hsn_no: item.hsn_no || "",
      expiry_date: item.expiry_date || "",
      quantity: item.pack_quantity || item.quantity || 1,
      units: item.units_per_pack || 1,
      rate_pack: item.pack_price || 0,
      mrp_pack:
        item.mrp_pack || (item.mrp_per_unit || 0) * (item.units_per_pack || 1),
      total_amount:
        item.item_total !== undefined && item.item_total !== null
          ? String(item.item_total)
          : (
              (item.pack_quantity || item.quantity || 1) *
              (item.pack_price || 0) *
              (1 +
                ((parseFloat(item.cgst) || 0) + (parseFloat(item.sgst) || 0)) /
                  100)
            ).toFixed(2),
      cgst: item.cgst !== undefined ? item.cgst : "",
      sgst: item.sgst !== undefined ? item.sgst : "",
    }));

    setPurchaseItems(mappedItems);
  };

  const handleSaveEditPurchase = async () => {
    if (!editingPurchase) return;

    setSubmitting(true);
    try {
      await axios.put(
        `${API}/purchases/${editingPurchase.id}?update_inventory=true`,
        {
          supplier_id: editingPurchase.supplier_id,
          supplier_name: editingPurchase.supplier_name,
          invoice_no: editingPurchase.invoice_no,
          purchase_date: editingPurchase.purchase_date,
          items: editingPurchase.items.map(({ id, _editing, ...item }) => ({
            product_id: item.product_id || `prod-${Date.now()}`,
            product_name: item.product_name,
            batch_no: item.batch_no,
            expiry_date: item.expiry_date,
            pack_quantity: parseInt(item.pack_quantity) || 1,
            units_per_pack: parseInt(item.units_per_pack) || 1,
            pack_price: parseFloat(item.pack_price) || 0,
            mrp_per_unit: parseFloat(item.mrp_per_unit) || 0,
            hsn_no: item.hsn_no || null,
          })),
        }
      );

      toast.success("Purchase updated successfully");
      setEditingPurchase(null);
      await fetchPurchases(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update purchase");
    } finally {
      setSubmitting(false);
    }
  };

  // ============ DELETE PURCHASE ============

  const handleDeletePurchase = async (deleteInventory = false) => {
    if (!deleteDialog.purchase) return;

    try {
      const response = await axios.delete(
        `${API}/purchases/${deleteDialog.purchase.id}?delete_inventory=${deleteInventory}`
      );
      toast.success(response.data.message);
      if (deleteInventory && response.data.deleted_inventory_items > 0) {
        toast.info(
          `${response.data.deleted_inventory_items} inventory items also deleted`
        );
      }
      setDeleteDialog({ open: false, purchase: null });
      await fetchPurchases(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete purchase");
    }
  };

  // ============ CSV IMPORT ============

  const handleCsvFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/purchases/csv-columns`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setCsvColumns(response.data.columns);
      setCsvSampleData(response.data.sample_data);
    } catch (error) {
      toast.error("Failed to read CSV file");
    }
  };

  const handleCsvImport = async () => {
    if (!selectedSupplier) {
      toast.error("Please select a supplier");
      return;
    }
    if (!csvFile) {
      toast.error("Please select a CSV file");
      return;
    }
    if (
      !csvMapping.product_name_col ||
      !csvMapping.batch_no_col ||
      !csvMapping.quantity_col ||
      !csvMapping.purchase_price_col ||
      !csvMapping.mrp_col ||
      !csvMapping.expiry_date_col
    ) {
      toast.error("Please map all required fields");
      return;
    }

    const supplier = suppliers.find((s) => s.id === selectedSupplier);
    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("supplier_id", selectedSupplier);
    formData.append("supplier_name", supplier?.name || "Unknown");
    formData.append("product_name_col", csvMapping.product_name_col);
    formData.append("batch_no_col", csvMapping.batch_no_col);
    formData.append("expiry_date_col", csvMapping.expiry_date_col);
    formData.append("quantity_col", csvMapping.quantity_col);
    formData.append("purchase_price_col", csvMapping.purchase_price_col);
    formData.append("mrp_col", csvMapping.mrp_col);
    if (csvMapping.hsn_col && csvMapping.hsn_col !== "none") {
      formData.append("hsn_col", csvMapping.hsn_col);
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/purchases/import-csv`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success(
        `CSV imported: ${response.data.items_imported} items added`
      );
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }

      setCsvDialog(false);
      setCsvFile(null);
      setCsvColumns([]);
      setCsvSampleData([]);
      setCsvMapping({
        product_name_col: "",
        batch_no_col: "",
        expiry_date_col: "",
        quantity_col: "",
        purchase_price_col: "",
        mrp_col: "",
        hsn_col: "",
      });
      await fetchPurchases(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || "CSV import failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate total - based on row totals (which include CGST/SGST and manual overrides)
  const totalAmount = purchaseItems.reduce((sum, item) => {
    return sum + (parseFloat(item.total_amount) || 0);
  }, 0);

  // Calculate total units
  const totalUnits = purchaseItems.reduce((sum, item) => {
    const qty = parseInt(item.quantity) || parseInt(item.pack_quantity) || 0;
    const units = parseInt(item.units) || parseInt(item.units_per_pack) || 1;
    return sum + qty * units;
  }, 0);

  // Calculate total packs
  const totalPacks = purchaseItems.reduce((sum, item) => {
    const qty = parseInt(item.quantity) || parseInt(item.pack_quantity) || 0;
    return sum + qty;
  }, 0);

  useEffect(() => {
    if (showSuggestions && highlightedSuggestionIndex >= 0) {
      const el = document.getElementById(
        `purchases-suggestion-${highlightedSuggestionIndex}`
      );
      if (el) el.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedSuggestionIndex, showSuggestions]);

  // ============ ADD PARTIAL PAYMENT ============
  const handleAddNewPayment = async () => {
    if (!newPaymentAmount || parseFloat(newPaymentAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/purchases/${paymentDialog.purchase.id}/payments`,
        {
          amount: parseFloat(newPaymentAmount),
          notes: newPaymentNotes,
        }
      );
      toast.success("Payment added successfully");
      setPaymentDialog({ open: false, purchase: null });
      setNewPaymentAmount("");
      setNewPaymentNotes("");
      await fetchPurchases(pagination.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add payment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkAsPaid = async (purchase) => {
    try {
      const remainingAmount =
        purchase.total_amount - (purchase.amount_paid || 0);
      if (remainingAmount <= 0) return;

      await axios.post(`${API}/purchases/${purchase.id}/payments`, {
        amount: remainingAmount,
        notes: "Marked as Paid in Full",
      });
      toast.success("Purchase marked as Paid");
      await fetchPurchases(pagination.page);
    } catch (error) {
      toast.error("Failed to mark as paid");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div
      className="space-y-6 animate-fade-in pb-24"
      data-testid="purchases-page"
    >
      {/* Restore Draft Dialog */}
      <Dialog
        open={paymentDialog.open}
        onOpenChange={(open) =>
          !open && setPaymentDialog({ open: false, purchase: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Partial Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={newPaymentAmount}
                onChange={(e) => setNewPaymentAmount(e.target.value)}
              />
              {paymentDialog.purchase && (
                <p className="text-xs text-muted-foreground">
                  Remaining Balance: ₹
                  {(
                    paymentDialog.purchase.total_amount -
                    (paymentDialog.purchase.amount_paid || 0)
                  ).toFixed(2)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                placeholder="Payment via UPI, Bank Transfer..."
                value={newPaymentNotes}
                onChange={(e) => setNewPaymentNotes(e.target.value)}
              />
            </div>
            <Button
              className="w-full btn-primary"
              onClick={handleAddNewPayment}
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Payment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Keyboard Shortcuts Dialog */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="w-5 h-5" />
              Keyboard Shortcuts
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-4">
            <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
              <span>New Purchase</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">
                {getOS() === "mac" ? "⌥ + N" : "Alt + N"}
              </kbd>
            </div>
            <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
              <span>Add Item</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">
                {getOS() === "mac" ? "⌥ + A" : "Alt + A"}
              </kbd>
            </div>
            <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
              <span>Save Purchase</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">
                {getOS() === "mac" ? "⌘ + Enter" : "Ctrl + Enter"}
              </kbd>
            </div>
            <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
              <span>Save Item / Next Field</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">
                Enter / Tab
              </kbd>
            </div>
            <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
              <span>Fill Default (on empty field)</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">
                Tab
              </kbd>
            </div>
            <div className="flex justify-between items-center p-2 bg-muted/30 rounded">
              <span>Cancel / Close</span>
              <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">
                Escape
              </kbd>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Purchases</h1>
          <p className="text-muted-foreground">
            {pagination.total} purchases recorded • Items tracked in units
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowShortcuts(true)}
            data-testid="shortcuts-btn"
          >
            <Keyboard className="w-4 h-4 mr-2" />
            Shortcuts
          </Button>

          <Button
            variant="outline"
            className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30 hover:border-primary/50"
            onClick={() => window.open("/scan", "_blank")}
            data-testid="scan-products-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Scan
          </Button>

          <Dialog open={csvDialog} onOpenChange={setCsvDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="csv-import-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Import Purchases from CSV</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 pt-4">
                <div className="space-y-2">
                  <Label>Supplier *</Label>
                  <SupplierSelector
                    selectedId={selectedSupplier}
                    knownSuppliers={suppliers}
                    onSelect={(s) => {
                      setSelectedSupplier(s.id);
                      if (!suppliers.find((x) => x.id === s.id)) {
                        setSuppliers((prev) => [...prev, s]);
                      }
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label>CSV File *</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileSelect}
                  />
                </div>

                {csvColumns.length > 0 && (
                  <div className="space-y-4">
                    <h4 className="font-medium">Map CSV Columns</h4>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: "product_name_col", label: "Product Name *" },
                        { key: "batch_no_col", label: "Batch Number *" },
                        { key: "expiry_date_col", label: "Expiry Date *" },
                        { key: "quantity_col", label: "Quantity (Units) *" },
                        {
                          key: "purchase_price_col",
                          label: "Purchase Price/Unit *",
                        },
                        { key: "mrp_col", label: "MRP/Unit *" },
                      ].map(({ key, label }) => (
                        <div key={key} className="space-y-2">
                          <Label>{label}</Label>
                          <Select
                            value={csvMapping[key]}
                            onValueChange={(v) =>
                              setCsvMapping({ ...csvMapping, [key]: v })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select column" />
                            </SelectTrigger>
                            <SelectContent>
                              {csvColumns.map((col) => (
                                <SelectItem key={col} value={col}>
                                  {col}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                      <div className="space-y-2">
                        <Label>HSN Code (optional)</Label>
                        <Select
                          value={csvMapping.hsn_col}
                          onValueChange={(v) =>
                            setCsvMapping({ ...csvMapping, hsn_col: v })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select column" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {csvColumns.map((col) => (
                              <SelectItem key={col} value={col}>
                                {col}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleCsvImport}
                  disabled={submitting || csvColumns.length === 0}
                  className="w-full btn-primary"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Button
            className="btn-primary"
            onClick={handleStartNewPurchase}
            data-testid="add-purchase-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {getOS() === "mac" ? "New (⌥N)" : "New (Alt+N)"}
          </Button>
        </div>
      </div>

      {/* New Purchase - Inline Table Entry with UNIT-BASED fields */}
      {showNewPurchase && (
        <Card
          className="glass bg-card/45 backdrop-blur-xl border border-primary/30 relative overflow-hidden shadow-2xl rounded-2xl"
          data-testid="new-purchase-form"
        >
          {isAiLoading && (
            <div className="absolute inset-0 z-[100] flex flex-col items-center justify-center bg-background/60 backdrop-blur-md transition-all duration-300">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full"></div>
                <Loader2 className="w-12 h-12 text-primary animate-spin relative z-10" />
              </div>
              <p className="mt-5 text-lg font-extrabold text-foreground tracking-wide animate-pulse">
                Running AI Analysis...
              </p>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                Extracting properties
              </p>
            </div>
          )}
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  New Purchase
                  <span className="text-xs font-normal text-muted-foreground ml-2">
                    (
                    <kbd className="px-1 bg-muted rounded font-mono text-[10px]">
                      Enter
                    </kbd>{" "}
                    to save)
                  </span>
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Button
                    variant={aiAutofillEnabled ? "default" : "outline"}
                    onClick={() => setAiAutofillEnabled(!aiAutofillEnabled)}
                    className={`relative overflow-hidden group ${
                      aiAutofillEnabled
                        ? "bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-500/30 border-none transition-all duration-300 font-bold"
                        : "text-indigo-600 border-indigo-200 hover:bg-indigo-50 transition-all font-medium"
                    }`}
                    size="sm"
                  >
                    {aiAutofillEnabled && (
                      <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out skew-x-12"></div>
                    )}
                    ✨ AI Autofill {aiAutofillEnabled ? "ON" : "OFF"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleCancelNewPurchase}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Warning Text Dropdown */}
              <div
                className={`flex justify-end pr-10 transition-all duration-300 overflow-hidden ${aiAutofillEnabled ? "max-h-12 opacity-100" : "max-h-0 opacity-0"}`}
              >
                <div className="text-[11px] font-medium text-amber-600 dark:text-amber-400 bg-amber-500/10 px-3 py-1.5 rounded-md border border-amber-500/20 flex items-center gap-1.5 shadow-sm backdrop-blur-sm">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Generative AI may be inaccurate. Please review the autofilled
                  details below before saving.
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Supplier & Invoice & Payment Tracking */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2 lg:col-span-1">
                <Label>Supplier *</Label>
                <SupplierSelector
                  selectedId={selectedSupplier}
                  knownSuppliers={suppliers}
                  onSelect={(s) => {
                    setSelectedSupplier(s.id);
                    if (!suppliers.find((x) => x.id === s.id)) {
                      setSuppliers((prev) => [...prev, s]);
                    }
                  }}
                />
              </div>
              <div className="space-y-2 lg:col-span-1">
                <Label>Invoice Number</Label>
                <Input
                  placeholder="INV-001"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                  data-testid="invoice-no-input"
                />
              </div>
              <div className="space-y-2 lg:col-span-1">
                <Label>Purchase Date *</Label>
                <Input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                />
              </div>

              <div className="text-center p-2 rounded-lg bg-muted/50">
                <span className="text-xs text-muted-foreground">
                  Total Units
                </span>
                <div className="text-lg font-bold text-primary">
                  {totalUnits}
                </div>
              </div>
              <div className="text-center p-2 rounded-lg bg-primary/10 border border-primary/20 shadow-sm">
                <span className="text-[10px] uppercase font-bold text-primary opacity-80">
                  Total Amount
                </span>
                <div className="text-xl font-bold font-mono text-primary leading-none mt-1">
                  ₹{totalAmount.toFixed(2)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 pt-2 border-t border-border mt-2">
              <div className="space-y-2 lg:col-span-2">
                <Label>Payment Status</Label>
                <div className="flex bg-muted/50 p-1 rounded-lg">
                  {["Unpaid", "Partial", "Paid"].map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => setPaymentStatus(status)}
                      className={`flex-1 text-xs font-semibold py-1.5 px-3 rounded-md transition-all ${
                        paymentStatus === status
                          ? status === "Paid"
                            ? "bg-green-500 text-white shadow-sm"
                            : status === "Unpaid"
                              ? "bg-red-500 text-white shadow-sm"
                              : "bg-yellow-500 text-white shadow-sm"
                          : "text-muted-foreground hover:bg-background/50"
                      }`}
                    >
                      {status === "Paid" ? "Fully Paid" : status}
                    </button>
                  ))}
                </div>
              </div>

              {paymentStatus === "Partial" && (
                <div className="space-y-2 lg:col-span-2">
                  <Label>Initial Amount Paid (₹)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amountPaid}
                    onChange={(e) => {
                      const val = e.target.value;
                      const numericVal = parseFloat(val);
                      if (numericVal && numericVal >= totalAmount) {
                        setPaymentStatus("Paid");
                        setAmountPaid("");
                        toast.success(
                          "Amount covers total. Marked as Fully Paid."
                        );
                      } else {
                        setAmountPaid(val);
                      }
                    }}
                    placeholder="e.g. 500"
                    className="border-yellow-500/50 focus-visible:ring-yellow-500/30"
                  />
                  {amountPaid && (
                    <p className="text-[11px] text-muted-foreground">
                      Remaining:{" "}
                      <span className="font-bold text-red-400">
                        ₹{(totalAmount - parseFloat(amountPaid)).toFixed(2)}
                      </span>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Items Table - Inline Editable with enhanced fields */}
            <div className="border border-border rounded-lg relative overflow-hidden">
              <Table wrapperClassName="h-[350px]">
                <TableHeader className="sticky top-0 bg-muted/95 backdrop-blur z-[50]">
                  <TableRow>
                    <TableHead className="w-[150px] font-bold text-foreground">
                      Product *
                    </TableHead>
                    <TableHead className="w-[100px] font-bold text-foreground">
                      MFG.
                    </TableHead>
                    <TableHead className="w-[100px] font-bold text-foreground">
                      Salt
                    </TableHead>
                    <TableHead className="w-[80px] font-bold text-foreground">
                      Pack Type
                    </TableHead>
                    <TableHead className="w-[80px] font-bold text-foreground">
                      Batch
                    </TableHead>
                    <TableHead className="w-[80px] font-bold text-foreground">
                      HSN
                    </TableHead>
                    <TableHead className="w-[100px] font-bold text-foreground">
                      Expiry
                    </TableHead>
                    <TableHead className="w-[95px] text-center font-bold text-foreground">
                      <div className="flex flex-col items-center gap-1 py-1">
                        <span>CGST (%)</span>
                        <label className="flex items-center gap-1 cursor-pointer select-none text-[10px] font-medium text-muted-foreground/80 hover:text-foreground">
                          <input
                            type="checkbox"
                            checked={applyCgstToAll}
                            onChange={(e) =>
                              handleApplyCgstToAllChange(e.target.checked)
                            }
                            className="h-3.5 w-3.5 rounded border border-border/80 text-primary focus:ring-1 focus:ring-primary cursor-pointer transition-colors"
                          />
                          Apply All
                        </label>
                      </div>
                    </TableHead>
                    <TableHead className="w-[95px] text-center font-bold text-foreground">
                      <div className="flex flex-col items-center gap-1 py-1">
                        <span>SGST (%)</span>
                        <label className="flex items-center gap-1 cursor-pointer select-none text-[10px] font-medium text-muted-foreground/80 hover:text-foreground">
                          <input
                            type="checkbox"
                            checked={applySgstToAll}
                            onChange={(e) =>
                              handleApplySgstToAllChange(e.target.checked)
                            }
                            className="h-3.5 w-3.5 rounded border border-border/80 text-primary focus:ring-1 focus:ring-primary cursor-pointer transition-colors"
                          />
                          Apply All
                        </label>
                      </div>
                    </TableHead>
                    <TableHead className="w-[60px] text-center font-bold text-foreground">
                      Qty *
                    </TableHead>
                    <TableHead className="w-[60px] text-center font-bold text-foreground">
                      Units
                    </TableHead>
                    <TableHead className="w-[60px] text-center font-bold text-foreground">
                      T.Units
                    </TableHead>
                    <TableHead className="w-[80px] text-center font-bold text-foreground">
                      Rate(Pack)*
                    </TableHead>

                    <TableHead className="w-[80px] text-center font-bold text-foreground">
                      MRP(Pack)
                    </TableHead>
                    <TableHead className="w-[70px] text-center font-bold text-foreground">
                      MRP/Unit
                    </TableHead>
                    <TableHead className="w-[80px] text-center font-bold text-foreground">
                      Total *
                    </TableHead>
                    <TableHead className="w-[70px] text-center font-bold text-foreground">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Editable Item Rows */}
                  {purchaseItems.map((item, index) => {
                    const qty =
                      parseInt(item.quantity) ||
                      parseInt(item.pack_quantity) ||
                      1;
                    const units =
                      parseInt(item.units) ||
                      parseInt(item.units_per_pack) ||
                      1;
                    const ratePack =
                      parseFloat(item.rate_pack) ||
                      parseFloat(item.pack_price) ||
                      0;
                    const mrpPack =
                      parseFloat(item.mrp_pack) ||
                      parseFloat(item.mrp_per_unit) * units ||
                      0;
                    const totalUnits = qty * units;
                    const mrpUnit = units > 0 ? mrpPack / units : 0;
                    const totalAmount =
                      parseFloat(item.total_amount) || qty * ratePack;

                    return (
                      <TableRow
                        key={item.id || index}
                        className={`bg-primary/5 ${
                          priceAlerts[item.id] ? "animate-row-alert" : ""
                        }`}
                      >
                        <TableCell
                          className="relative"
                          style={{ overflow: "visible" }}
                        >
                          <div className="flex items-center gap-2">
                            <Input
                              ref={(el) => {
                                setInputRef(el, item.id);
                                if (index === purchaseItems.length - 1) {
                                  productInputRef.current = el;
                                }
                              }}
                              value={item.product_name || ""}
                              onChange={(e) => {
                                handleItemFieldChange(
                                  item.id,
                                  "product_name",
                                  e.target.value
                                );
                                setHighlightedSuggestionIndex(-1);
                              }}
                              onFocus={() => {
                                setSearchMedicine(item.product_name || "");
                                setShowSuggestions(true);
                                setActiveItemId(item.id);
                                setHighlightedSuggestionIndex(-1);
                              }}
                              onBlur={(e) => {
                                const currentId = item.id;
                                setTimeout(() => {
                                  if (!isMouseOverSuggestions) {
                                    setActiveItemId((prev) => {
                                      if (prev === currentId) {
                                        if (
                                          aiAutofillEnabled &&
                                          item.product_name &&
                                          item.product_name.length > 2 &&
                                          !item._inventoryMeta &&
                                          !item.salt_composition &&
                                          !item.manufacturer
                                        ) {
                                          handleSelectAiMedicineForItem(
                                            item.id,
                                            item.product_name
                                          );
                                        }
                                        setShowSuggestions(false);
                                        setHighlightedSuggestionIndex(-1);
                                        return null;
                                      }
                                      return prev;
                                    });
                                  }
                                }, 200);
                              }}
                              onKeyDown={(e) => {
                                const hasAiOption = searchMedicine.length > 1;
                                const maxIndex = hasAiOption
                                  ? medicineSuggestions.length
                                  : medicineSuggestions.length - 1;

                                if (
                                  !showSuggestions ||
                                  (medicineSuggestions.length === 0 &&
                                    !hasAiOption)
                                )
                                  return;

                                if (e.key === "ArrowDown") {
                                  e.preventDefault();
                                  setHighlightedSuggestionIndex((prev) =>
                                    prev < maxIndex ? prev + 1 : prev
                                  );
                                } else if (e.key === "ArrowUp") {
                                  e.preventDefault();
                                  setHighlightedSuggestionIndex((prev) =>
                                    prev > 0 ? prev - 1 : -1
                                  );
                                } else if (
                                  e.key === "Enter" &&
                                  highlightedSuggestionIndex >= 0
                                ) {
                                  e.preventDefault();
                                  if (
                                    highlightedSuggestionIndex ===
                                    medicineSuggestions.length
                                  ) {
                                    // Trigger AI explicitly
                                    handleSelectAiMedicineForItem(
                                      item.id,
                                      searchMedicine
                                    );
                                  } else {
                                    handleSelectMedicineForItem(
                                      item.id,
                                      medicineSuggestions[
                                        highlightedSuggestionIndex
                                      ]
                                    );
                                  }
                                  setHighlightedSuggestionIndex(-1);
                                } else if (e.key === "Escape") {
                                  setShowSuggestions(false);
                                  setHighlightedSuggestionIndex(-1);
                                }
                              }}
                              placeholder="Search..."
                              className={`h-8 text-xs ${
                                item._inventoryMeta
                                  ? item._inventoryMeta.stock_status ===
                                    "In Stock"
                                    ? "border-blue-300 bg-blue-50/30"
                                    : "border-orange-300 bg-orange-50/30"
                                  : ""
                              }`}
                              data-testid={`item-name-${index}`}
                              autoComplete="off"
                            />

                            {/* Inventory Indicator Icon */}
                            {item._inventoryMeta && (
                              <CustomTooltip
                                position="top"
                                text={
                                  item._inventoryMeta.stock_status ===
                                  "In Stock"
                                    ? `✅ In Stock: ${item._inventoryMeta.available_quantity} units\nBatch: ${item._inventoryMeta.batch_no || "N/A"}\nLast Supplier: ${item._inventoryMeta.last_supplier || "Unknown"}`
                                    : `⚠️ Out of Stock\nLast Supplier: ${item._inventoryMeta.last_supplier || "Unknown"}`
                                }
                              >
                                <Package
                                  className={`w-4 h-4 ${
                                    item._inventoryMeta.stock_status ===
                                    "In Stock"
                                      ? "text-blue-500"
                                      : "text-orange-500"
                                  }`}
                                />
                              </CustomTooltip>
                            )}

                            {/* Fuzzy Match Warning */}
                            {/* {item._inventoryMeta?.match_quality ===
                                "fuzzy" && (
                                <CustomTooltip
                                  position="top"
                                  text="Fuzzy match - please verify details"
                                >
                                  <span className="text-yellow-500 text-xs">
                                    ⚠️
                                  </span>
                                </CustomTooltip>
                              )} */}
                          </div>

                          {/* ========================================== */}
                          {/* SUGGESTIONS DROPDOWN VIA PORTAL            */}
                          {/* ========================================== */}
                          {showSuggestions &&
                            activeItemId === item.id &&
                            (medicineSuggestions.length > 0 ||
                              searchMedicine.length > 1) &&
                            createPortal(
                              <div
                                data-suggestions-dropdown="true"
                                className="bg-card/95 backdrop-blur-xl border border-border/80 rounded-xl shadow-2xl overflow-y-auto z-[99999]"
                                onMouseEnter={() =>
                                  setIsMouseOverSuggestions(true)
                                }
                                onMouseLeave={() => {
                                  setIsMouseOverSuggestions(false);
                                  const activeInput =
                                    inputRefs.current[activeItemId];
                                  if (
                                    activeInput &&
                                    document.activeElement !== activeInput
                                  ) {
                                    setShowSuggestions(false);
                                    setActiveItemId(null);
                                  }
                                }}
                                style={{
                                  position: "fixed",
                                  top: dropdownPosition.top,
                                  left: dropdownPosition.left,
                                  width: dropdownPosition.width || 480,
                                  maxHeight: "300px",
                                  transform:
                                    dropdownPosition.transform || "none",
                                  overscrollBehavior: "contain",
                                  WebkitOverflowScrolling: "touch",
                                }}
                                onScroll={handleScrollSuggestions}
                              >
                                {/* Match Quality Summary */}
                                {medicineSuggestions[0]?.matchQuality && (
                                  <div className="px-3 py-2 bg-muted border-b border-border text-xs text-muted-foreground flex gap-3 sticky top-0 z-10">
                                    {/* <span>
                                        Exact:{" "}
                                        {
                                          medicineSuggestions.filter(
                                            (m) => m.matchQuality === "exact"
                                          ).length
                                        }
                                      </span> */}
                                    <span>
                                      Good:{" "}
                                      {
                                        medicineSuggestions.filter(
                                          (m) => m.matchQuality === "good"
                                        ).length
                                      }
                                    </span>
                                    {/* <span>
                                        Fuzzy:{" "}
                                        {
                                          medicineSuggestions.filter(
                                            (m) => m.matchQuality === "fuzzy"
                                          ).length
                                        }
                                      </span> */}
                                  </div>
                                )}

                                {medicineSuggestions.map((medicine, idx) => (
                                  <div
                                    key={idx}
                                    id={`purchases-suggestion-${idx}`}
                                    className={`p-3 cursor-pointer border-b border-border last:border-0 ${
                                      highlightedSuggestionIndex === idx
                                        ? "bg-primary/20"
                                        : "hover:bg-primary/10"
                                    }`}
                                    // In your suggestion items, prevent mousedown from causing blur
                                    onMouseDown={(e) => {
                                      e.preventDefault(); // ← This prevents the blur from firing at all!
                                      handleSelectMedicineForItem(
                                        item.id,
                                        medicine
                                      );
                                      setHighlightedSuggestionIndex(-1);
                                    }}
                                    onMouseEnter={() =>
                                      setHighlightedSuggestionIndex(idx)
                                    }
                                  >
                                    {/* Header: Name + Source Badge + Match Quality */}
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                                        {medicine.name}

                                        {/* Source Badge */}
                                        {medicine.source === "inventory" && (
                                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                                            Inventory
                                          </span>
                                        )}
                                        {medicine.source === "global" && (
                                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
                                            Not in Inventory
                                          </span>
                                        )}

                                        {/* Match Quality Indicator */}
                                        {/* {medicine.matchQuality ===
                                            "exact" && (
                                            <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">
                                              Exact
                                            </span>
                                          )} */}
                                        {/* {medicine.matchQuality ===
                                            "fuzzy" && (
                                            <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                                              Fuzzy
                                            </span>
                                          )} */}
                                      </div>

                                      {/* Fuzzy Score */}
                                      {/* {medicine.fuzzyScore &&
                                          medicine.fuzzyScore < 90 && (
                                            <span className="text-[10px] text-muted-foreground">
                                              {Math.round(medicine.fuzzyScore)}%
                                              match
                                            </span>
                                          )} */}
                                    </div>

                                    {/* Manufacturer & Composition */}
                                    <div className="text-xs text-muted-foreground">
                                      <span>
                                        {medicine.manufacturer ||
                                          medicine.manufacturer_name}
                                      </span>
                                      {(medicine.salt_composition ||
                                        medicine.short_composition1) && (
                                        <span className="ml-2 text-primary/70">
                                          (
                                          {(
                                            medicine.salt_composition ||
                                            medicine.short_composition1
                                          )?.slice(0, 35)}
                                          ...)
                                        </span>
                                      )}
                                    </div>

                                    {/* Inventory-Specific Info */}
                                    {medicine.source === "inventory" && (
                                      <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                                        <span
                                          className={`px-2 py-0.5 rounded font-medium ${
                                            medicine.stock_status === "In Stock"
                                              ? "bg-green-500/20 text-green-700"
                                              : "bg-red-500/20 text-red-700"
                                          }`}
                                        >
                                          {medicine.stock_status}:{" "}
                                          {medicine.available_quantity || 0}{" "}
                                          units
                                        </span>

                                        {medicine.batch_no && (
                                          <span className="text-muted-foreground">
                                            Batch: {medicine.batch_no}
                                          </span>
                                        )}

                                        {medicine.expiry_date && (
                                          <span
                                            className={`${
                                              new Date(medicine.expiry_date) <
                                              new Date(
                                                Date.now() +
                                                  90 * 24 * 60 * 60 * 1000
                                              )
                                                ? "text-orange-600 font-medium"
                                                : "text-muted-foreground"
                                            }`}
                                          >
                                            Exp: {medicine.expiry_date}
                                            {new Date(medicine.expiry_date) <
                                              new Date() && " ⚠️ Expired"}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Pricing Info Row */}
                                    <div className="mt-2 flex items-center gap-3 text-xs">
                                      {medicine.source === "inventory" ? (
                                        <>
                                          <span className="font-mono font-medium text-primary">
                                            Purchase: ₹
                                            {Number(
                                              medicine.purchase_price || 0
                                            ).toFixed(2)}
                                            /unit
                                          </span>
                                          <span className="font-mono text-muted-foreground">
                                            MRP: ₹
                                            {Number(
                                              medicine.mrp_per_unit ||
                                                medicine.mrp ||
                                                0
                                            ).toFixed(2)}
                                          </span>
                                          {medicine.supplier_name && (
                                            <span className="text-blue-600">
                                              Last: {medicine.supplier_name}
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <span className="font-mono font-medium text-primary">
                                          MRP: ₹{medicine["price(₹)"] || 0} •{" "}
                                          {medicine.pack_size_label || "N/A"}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                {loadingSuggestions && (
                                  <div className="p-3 text-center border-t border-border">
                                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                                  </div>
                                )}

                                {/* AI Fallback Option */}
                                {!loadingSuggestions &&
                                  searchMedicine.length > 1 && (
                                    <div
                                      id={`purchases-suggestion-${medicineSuggestions.length}`}
                                      className={`p-3 cursor-pointer border-t border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/50 dark:bg-indigo-500/10 hover:bg-indigo-100/50 dark:hover:bg-indigo-500/20 transition-colors ${
                                        highlightedSuggestionIndex ===
                                        medicineSuggestions.length
                                          ? "bg-indigo-100/80 dark:bg-indigo-500/30"
                                          : ""
                                      }`}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        handleSelectAiMedicineForItem(
                                          item.id,
                                          searchMedicine
                                        );
                                      }}
                                      onMouseEnter={() =>
                                        setHighlightedSuggestionIndex(
                                          medicineSuggestions.length
                                        )
                                      }
                                    >
                                      <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-400 font-semibold text-sm">
                                        <div className="p-1.5 bg-indigo-100 dark:bg-indigo-500/20 rounded-md shadow-sm">
                                          ✨
                                        </div>
                                        <span>
                                          Get AI facts for{" "}
                                          <span className="font-bold underline decoration-indigo-300 dark:decoration-indigo-600 underline-offset-2">
                                            "{searchMedicine}"
                                          </span>
                                        </span>
                                      </div>
                                      <p className="text-[10.5px] font-medium text-indigo-500/80 dark:text-indigo-400/70 mt-1 ml-9">
                                        Bypass search and instantly extract
                                        properties
                                      </p>
                                    </div>
                                  )}
                              </div>,
                              document.body
                            )}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.manufacturer || ""}
                            onChange={(e) =>
                              handleItemFieldChange(
                                item.id,
                                "manufacturer",
                                e.target.value
                              )
                            }
                            placeholder="MFG"
                            className="h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            id={`add-salt-${item.id}`}
                            type="button"
                            variant="outline"
                            className="h-8 text-xs w-20 justify-start truncate"
                            onClick={() =>
                              setSaltDialog({
                                open: true,
                                itemId: item.id,
                                value: item.salt_composition || "",
                              })
                            }
                          >
                            {item.salt_composition
                              ? item.salt_composition.length > 7
                                ? item.salt_composition.slice(0, 7) + "..."
                                : item.salt_composition
                              : "Add Salt"}
                          </Button>
                        </TableCell>

                        <TableCell>
                          <Select
                            value={item.pack_type || "Strip"}
                            onValueChange={(v) =>
                              handleItemFieldChange(item.id, "pack_type", v)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PACK_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            id={`batch-${item.id}`}
                            value={item.batch_no || ""}
                            onChange={(e) =>
                              handleItemFieldChange(
                                item.id,
                                "batch_no",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                document
                                  .getElementById(`hsn-${item.id}`)
                                  ?.focus({ preventScroll: true });
                              }
                            }}
                            placeholder="Batch"
                            className="h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            id={`hsn-${item.id}`}
                            value={item.hsn_no || ""}
                            onChange={(e) =>
                              handleItemFieldChange(
                                item.id,
                                "hsn_no",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                document
                                  .getElementById(`expiry-${item.id}`)
                                  ?.focus({ preventScroll: true });
                              }
                            }}
                            placeholder="HSN"
                            className="h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            id={`expiry-${item.id}`}
                            type="date"
                            value={item.expiry_date || ""}
                            onChange={(e) =>
                              handleItemFieldChange(
                                item.id,
                                "expiry_date",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                document
                                  .getElementById(`cgst-${item.id}`)
                                  ?.focus({ preventScroll: true });
                              }
                            }}
                            className="h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            id={`cgst-${item.id}`}
                            type="number"
                            step="0.01"
                            value={item.cgst !== undefined ? item.cgst : ""}
                            onChange={(e) =>
                              handleItemFieldChangeWithCalc(
                                item.id,
                                "cgst",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                document
                                  .getElementById(`sgst-${item.id}`)
                                  ?.focus({ preventScroll: true });
                              }
                            }}
                            placeholder="0"
                            className="h-8 text-xs text-center w-14"
                            min="0"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            id={`sgst-${item.id}`}
                            type="number"
                            step="0.01"
                            value={item.sgst !== undefined ? item.sgst : ""}
                            onChange={(e) =>
                              handleItemFieldChangeWithCalc(
                                item.id,
                                "sgst",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                document
                                  .getElementById(`qty-${item.id}`)
                                  ?.focus({ preventScroll: true });
                              }
                            }}
                            placeholder="0"
                            className="h-8 text-xs text-center w-14"
                            min="0"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            id={`qty-${item.id}`}
                            type="number"
                            value={item.quantity || item.pack_quantity || ""}
                            onChange={(e) =>
                              handleItemFieldChangeWithCalc(
                                item.id,
                                "quantity",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                document
                                  .getElementById(`units-${item.id}`)
                                  ?.focus({ preventScroll: true });
                              }
                            }}
                            placeholder="1"
                            className="h-8 text-xs text-center w-14"
                            min="1"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            id={`units-${item.id}`}
                            type="number"
                            value={item.units || item.units_per_pack || ""}
                            onChange={(e) =>
                              handleItemFieldChange(
                                item.id,
                                "units",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                document
                                  .getElementById(`rate-${item.id}`)
                                  ?.focus({ preventScroll: true });
                              }
                            }}
                            placeholder="1"
                            className="h-8 text-xs text-center w-14"
                            min="1"
                          />
                        </TableCell>
                        <TableCell className="text-center font-mono text-xs font-medium text-primary">
                          {totalUnits}
                        </TableCell>
                        <TableCell
                          className="relative"
                          style={{ position: "relative" }}
                        >
                          <div className="flex items-center gap-2">
                            <Input
                              id={`rate-${item.id}`}
                              type="number"
                              step="0.01"
                              value={item.rate_pack || item.pack_price || ""}
                              onChange={(e) =>
                                handleItemFieldChangeWithCalc(
                                  item.id,
                                  "rate_pack",
                                  e.target.value
                                )
                              }
                              onBlur={() =>
                                checkPriceHistorySilent(
                                  item.id,
                                  item.product_name,
                                  item.rate_pack || item.pack_price
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  document
                                    .getElementById(`mrp-${item.id}`)
                                    ?.focus({ preventScroll: true });
                                }
                              }}
                              placeholder="₹"
                              className={`h-8 text-xs text-center w-28 pr-6 ${
                                priceAlerts[item.id]
                                  ? "border-yellow-500 bg-yellow-500/10"
                                  : ""
                              }`}
                            />

                            {(item.rate_pack || item.pack_price) && (
                              <button
                                id={`price-history-${item.id}`}
                                type="button"
                                onClick={() => {
                                  const currentRate =
                                    parseFloat(item.rate_pack) ||
                                    parseFloat(item.pack_price) ||
                                    0;

                                  if (!item.product_name) {
                                    toast.error("Enter product name first");
                                    return;
                                  }

                                  checkPriceHistory(
                                    item.id,
                                    item.product_name,
                                    currentRate
                                  );
                                }}
                                className="absolute right-1 top-1/2 -translate-y-1/2 text-primary hover:text-primary/70"
                                title="Compare with past rates"
                              >
                                <CustomTooltip
                                  position="top"
                                  text="Compare with past rates"
                                >
                                  <ArrowUpDown
                                    className={`w-4 h-4 ${priceAlerts[item.id] ? "text-yellow-500 animate-icon-alert cursor-pointer" : ""}`}
                                  />
                                </CustomTooltip>
                              </button>
                            )}
                          </div>
                        </TableCell>

                        <TableCell>
                          <Input
                            id={`mrp-${item.id}`}
                            type="number"
                            step="0.01"
                            value={item.mrp_pack || ""}
                            onChange={(e) =>
                              handleItemFieldChange(
                                item.id,
                                "mrp_pack",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                document
                                  .getElementById(`total-${item.id}`)
                                  ?.focus({ preventScroll: true });
                              }
                            }}
                            placeholder="₹"
                            className="h-8 w-28 text-xs text-center"
                          />
                        </TableCell>
                        <TableCell className="text-center font-mono text-xs text-muted-foreground">
                          {mrpPack && units ? `₹${mrpUnit.toFixed(2)}` : "-"}
                        </TableCell>
                        <TableCell>
                          <Input
                            id={`total-${item.id}`}
                            type="number"
                            step="0.01"
                            value={item.total_amount || ""}
                            onChange={(e) =>
                              handleItemFieldChangeWithCalc(
                                item.id,
                                "total_amount",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (index === purchaseItems.length - 1) {
                                  handleAddNewRow();
                                } else {
                                  document
                                    .querySelector(
                                      `[data-testid="item-name-${index + 1}"]`
                                    )
                                    ?.focus({ preventScroll: true });
                                }
                              }
                            }}
                            placeholder="₹"
                            className="h-8 w-28 text-xs text-center "
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {purchaseItems.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={17}
                        className="text-center py-8 text-muted-foreground"
                      >
                        <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        No items. Click "Add Item" to add a row.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Hint for unit-based system */}
            <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
              💡 <strong>Tip:</strong> Enter Qty and either Rate(Pack) OR Total
              - the other will auto-calculate. Salt, HSN, Batch, Expiry are
              optional.
            </p>

            {/* Action Buttons - Add Item always visible */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddNewRow}
                data-testid="add-item-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add Item
              </Button>
              {/* Submit moved to fixed footer */}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Existing Purchase Modal */}
      {/* {editingPurchase && (
        <Dialog
          open={!!editingPurchase}
          onOpenChange={() => setEditingPurchase(null)}
        >
          <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>
                Edit Purchase -{" "}
                {editingPurchase.invoice_no || editingPurchase.id.slice(0, 8)}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4 pt-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Supplier</Label>
                  <SupplierSelector 
                    selectedId={editingPurchase.supplier_id}
                    knownSuppliers={suppliers}
                    onSelect={(s) => {
                      setEditingPurchase((prev) => ({
                        ...prev,
                        supplier_id: s.id,
                        supplier_name: s.name,
                      }));
                      if (!suppliers.find(x => x.id === s.id)) {
                        setSuppliers(prev => [...prev, s]);
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Invoice Number</Label>
                  <Input
                    value={editingPurchase.invoice_no || ""}
                    onChange={(e) =>
                      setEditingPurchase((prev) => ({
                        ...prev,
                        invoice_no: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Purchase Date</Label>
                  <Input
                    type="date"
                    value={editingPurchase.purchase_date || ""}
                    onChange={(e) =>
                      setEditingPurchase((prev) => ({
                        ...prev,
                        purchase_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="border border-border rounded-lg overflow-hidden">
                  <Table wrapperClassName="h-[400px]">
                    <TableHeader className="sticky top-0 bg-muted/95 backdrop-blur z-[50]">
                      <TableRow>
                        <TableHead>Product Name</TableHead>
                        <TableHead>Batch</TableHead>
                        <TableHead>Expiry</TableHead>
                        <TableHead className="text-center">Packs</TableHead>
                        <TableHead className="text-center">
                          Units/Pack
                        </TableHead>
                        <TableHead className="text-center">
                          Pack Price
                        </TableHead>
                        <TableHead className="text-center">MRP/Unit</TableHead>
                        <TableHead className="text-center">
                          Total Units
                        </TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {editingPurchase.items.map((item, idx) => {
                        const packQty = parseInt(item.pack_quantity) || 1;
                        const unitsPerPack = parseInt(item.units_per_pack) || 1;
                        const packPrice = parseFloat(item.pack_price) || 0;
                        const totalUnits = packQty * unitsPerPack;
                        const totalPrice = packQty * packPrice;

                        return (
                          <TableRow
                            key={idx}
                            className={`${
                              priceAlerts[item.id] ? "animate-row-alert" : ""
                            }`}
                          >
                            <TableCell>
                              <Input
                                value={item.product_name}
                                onChange={(e) => {
                                  const items = [...editingPurchase.items];
                                  items[idx] = {
                                    ...items[idx],
                                    product_name: e.target.value,
                                  };
                                  setEditingPurchase((prev) => ({
                                    ...prev,
                                    items,
                                  }));
                                }}
                                className="h-8 text-xs"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={item.batch_no}
                                onChange={(e) => {
                                  const items = [...editingPurchase.items];
                                  items[idx] = {
                                    ...items[idx],
                                    batch_no: e.target.value,
                                  };
                                  setEditingPurchase((prev) => ({
                                    ...prev,
                                    items,
                                  }));
                                }}
                                className="h-8 text-xs w-20"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="date"
                                value={item.expiry_date}
                                onChange={(e) => {
                                  const items = [...editingPurchase.items];
                                  items[idx] = {
                                    ...items[idx],
                                    expiry_date: e.target.value,
                                  };
                                  setEditingPurchase((prev) => ({
                                    ...prev,
                                    items,
                                  }));
                                }}
                                className="h-8 text-xs"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.pack_quantity}
                                onChange={(e) => {
                                  const items = [...editingPurchase.items];
                                  items[idx] = {
                                    ...items[idx],
                                    pack_quantity: e.target.value,
                                  };
                                  setEditingPurchase((prev) => ({
                                    ...prev,
                                    items,
                                  }));
                                }}
                                className="h-8 text-xs w-16 text-center"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={item.units_per_pack}
                                onChange={(e) => {
                                  const items = [...editingPurchase.items];
                                  items[idx] = {
                                    ...items[idx],
                                    units_per_pack: e.target.value,
                                  };
                                  setEditingPurchase((prev) => ({
                                    ...prev,
                                    items,
                                  }));
                                }}
                                className="h-8 text-xs w-16 text-center"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.pack_price}
                                onChange={(e) => {
                                  const items = [...editingPurchase.items];
                                  items[idx] = {
                                    ...items[idx],
                                    pack_price: e.target.value,
                                  };
                                  setEditingPurchase((prev) => ({
                                    ...prev,
                                    items,
                                  }));
                                }}
                                onBlur={() => checkPriceHistorySilent(item.id, item.product_name, item.pack_price)}
                                className={`h-8 text-xs w-20 text-center ${
                                  priceAlerts[item.id]
                                    ? "border-yellow-500 bg-yellow-500/10"
                                    : ""
                                }`}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.mrp_per_unit}
                                onChange={(e) => {
                                  const items = [...editingPurchase.items];
                                  items[idx] = {
                                    ...items[idx],
                                    mrp_per_unit: e.target.value,
                                  };
                                  setEditingPurchase((prev) => ({
                                    ...prev,
                                    items,
                                  }));
                                }}
                                className="h-8 text-xs w-20 text-center"
                              />
                            </TableCell>
                            <TableCell className="text-center font-mono font-medium text-primary">
                              {totalUnits}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              ₹{totalPrice.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditingPurchase(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEditPurchase}
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )} */}

      {/* Search and Filters */}
      {!showNewPurchase && editingPurchaseId === null && (
        <>
          <Card className="glass bg-card/45 backdrop-blur-xl border border-border/70 shadow-lg rounded-2xl p-5">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search purchases by supplier, invoice..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <SupplierSelector
                  selectedId={filterSupplier}
                  knownSuppliers={suppliers}
                  showAllOption={true}
                  className="w-48"
                  onSelect={(s) => {
                    handleSupplierFilterChange(s.id);
                    if (
                      s.id !== "all" &&
                      !suppliers.find((x) => x.id === s.id)
                    ) {
                      setSuppliers((prev) => [...prev, s]);
                    }
                  }}
                />
                <Input
                  type="date"
                  placeholder="Start date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
                <Input
                  type="date"
                  placeholder="End date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
                {(searchQuery ||
                  (filterSupplier && filterSupplier !== "all") ||
                  startDate ||
                  endDate) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery("");
                      setFilterSupplier("all");
                      setStartDate("");
                      setEndDate("");
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Purchases Table */}
          <Card className="glass bg-card/35 backdrop-blur-xl border border-border/70 shadow-lg rounded-2xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setSortBy("purchase_date");
                        setSortOrder(
                          sortBy === "purchase_date" && sortOrder === "desc"
                            ? "asc"
                            : "desc"
                        );
                      }}
                    >
                      Date <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setSortBy("supplier_name");
                        setSortOrder(
                          sortBy === "supplier_name" && sortOrder === "desc"
                            ? "asc"
                            : "desc"
                        );
                      }}
                      className="h-auto p-0 font-medium hover:bg-transparent"
                    >
                      Supplier <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setSortBy("total_amount");
                        setSortOrder(
                          sortBy === "total_amount" && sortOrder === "desc"
                            ? "asc"
                            : "desc"
                        );
                      }}
                      className="h-auto p-0 font-medium hover:bg-transparent"
                    >
                      Amount <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map((purchase) => (
                  <>
                    <TableRow
                      key={purchase.id}
                      id={`record-${purchase.id}`}
                      className="cursor-pointer"
                      onClick={() =>
                        setExpandedPurchase(
                          expandedPurchase === purchase.id ? null : purchase.id
                        )
                      }
                    >
                      <TableCell>
                        {expandedPurchase === purchase.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(
                          purchase.purchase_date ||
                            purchase.created_at?.slice(0, 10)
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {purchase.supplier_name}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {purchase.invoice_no || "-"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {purchase.items?.length || 0} items
                      </TableCell>
                      <TableCell className="text-center">
                        <span
                          className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            purchase.payment_status === "Paid"
                              ? "bg-green-500/10 text-green-500 border border-green-500/20"
                              : purchase.payment_status === "Partial"
                                ? "bg-yellow-500/10 text-yellow-500 border border-yellow-500/20"
                                : "bg-red-500/10 text-red-400 border border-red-500/20"
                          }`}
                        >
                          {purchase.payment_status || "Unpaid"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-primary">
                        ₹{purchase.total_amount?.toFixed(2)}
                      </TableCell>
                      <TableCell
                        className="text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex justify-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-primary"
                            onClick={() =>
                              handleGeneratePurchasePdf(purchase.id)
                            }
                            data-testid={`pdf-purchase-${purchase.id}`}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => handleStartEditPurchase(purchase)}
                            data-testid={`edit-purchase-${purchase.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive"
                            onClick={() =>
                              setDeleteDialog({ open: true, purchase })
                            }
                            data-testid={`delete-purchase-${purchase.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedPurchase === purchase.id && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={8} className="p-4">
                          <div className="rounded-lg border border-border overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Product</TableHead>
                                  <TableHead>Batch</TableHead>
                                  <TableHead>Expiry</TableHead>
                                  <TableHead className="text-center">
                                    Packs
                                  </TableHead>
                                  <TableHead className="text-center">
                                    Units/Pack
                                  </TableHead>
                                  <TableHead className="text-center">
                                    Total Units
                                  </TableHead>
                                  <TableHead className="text-center">
                                    Cost/Unit
                                  </TableHead>
                                  <TableHead className="text-center">
                                    MRP/Unit
                                  </TableHead>
                                  <TableHead className="text-center">
                                    CGST %
                                  </TableHead>
                                  <TableHead className="text-center">
                                    SGST %
                                  </TableHead>
                                  <TableHead className="text-right">
                                    Total
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {purchase.items?.map((item, idx) => {
                                  const packQty =
                                    item.pack_quantity || item.quantity || 1;
                                  const unitsPerPack = item.units_per_pack || 1;
                                  const totalUnits =
                                    item.total_units || packQty * unitsPerPack;
                                  const costPerUnit =
                                    item.price_per_unit ||
                                    item.purchase_price ||
                                    0;
                                  const mrpPerUnit =
                                    item.mrp_per_unit || item.mrp || 0;
                                  const packPrice =
                                    item.pack_price ||
                                    costPerUnit * unitsPerPack;
                                  const total =
                                    item.item_total || packQty * packPrice;

                                  return (
                                    <TableRow key={idx}>
                                      <TableCell className="font-medium">
                                        {item.product_name}
                                      </TableCell>
                                      <TableCell>{item.batch_no}</TableCell>
                                      <TableCell>{item.expiry_date}</TableCell>
                                      <TableCell className="text-center">
                                        {packQty}
                                      </TableCell>
                                      <TableCell className="text-center">
                                        {unitsPerPack}
                                      </TableCell>
                                      <TableCell className="text-center font-medium text-primary">
                                        {totalUnits}
                                      </TableCell>
                                      <TableCell className="text-center">
                                        ₹{costPerUnit.toFixed(2)}
                                      </TableCell>
                                      <TableCell className="text-center">
                                        ₹{mrpPerUnit.toFixed(2)}
                                      </TableCell>
                                      <TableCell className="text-center font-mono">
                                        {item.cgst !== undefined
                                          ? `${item.cgst}%`
                                          : "0%"}
                                      </TableCell>
                                      <TableCell className="text-center font-mono">
                                        {item.sgst !== undefined
                                          ? `${item.sgst}%`
                                          : "0%"}
                                      </TableCell>
                                      <TableCell className="text-right font-mono">
                                        ₹{total.toFixed(2)}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>

                          {/* Payment Timeline Section */}
                          <div className="mt-4 p-4 rounded-lg bg-card border border-border shadow-sm flex flex-col md:flex-row gap-6">
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm mb-4 flex items-center gap-2">
                                <CreditCard className="w-4 h-4 text-primary" />
                                Payment History
                              </h4>
                              {purchase.payments &&
                              purchase.payments.length > 0 ? (
                                <div className="space-y-4">
                                  {purchase.payments.map((payment, i) => (
                                    <div
                                      key={i}
                                      className="flex relative pl-5 before:absolute before:left-[7px] before:top-5 before:bottom-[-20px] last:before:hidden before:w-[2px] before:bg-muted-foreground/20"
                                    >
                                      <div className="absolute left-0 top-1.5 w-[16px] h-[16px] rounded-full bg-primary/20 flex items-center justify-center ring-4 ring-background">
                                        <div className="w-[8px] h-[8px] rounded-full bg-primary"></div>
                                      </div>
                                      <div>
                                        <p className="text-sm font-bold text-foreground">
                                          ₹{payment.amount.toFixed(2)}
                                        </p>
                                        <p className="text-xs text-muted-foreground font-medium">
                                          {new Date(
                                            payment.date
                                          ).toLocaleString()}{" "}
                                          <span className="mx-1">•</span>{" "}
                                          {payment.notes || "Partial Payment"}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-muted-foreground italic flex py-8 items-center justify-center bg-muted/20 rounded-lg border border-dashed border-border/50">
                                  No payment history recorded.
                                </div>
                              )}
                            </div>

                            <div className="w-full md:w-72 bg-muted/30 p-4 rounded-md border border-border/50 flex flex-col justify-center shadow-inner">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-sm text-muted-foreground">
                                  Total Billed:
                                </span>
                                <span className="text-sm font-mono font-medium">
                                  ₹{purchase.total_amount?.toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm text-muted-foreground">
                                  Amount Paid:
                                </span>
                                <span className="text-sm font-mono font-medium text-green-500">
                                  ₹{(purchase.amount_paid || 0).toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center pt-2 border-t border-border mb-4">
                                <span className="text-sm font-bold">
                                  Remaining:
                                </span>
                                <span className="text-base font-mono font-bold text-red-500">
                                  ₹
                                  {Math.max(
                                    0,
                                    purchase.total_amount -
                                      (purchase.amount_paid || 0)
                                  ).toFixed(2)}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                {purchase.payment_status !== "Paid" && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="flex-1 btn-primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPaymentDialog({
                                          open: true,
                                          purchase,
                                        });
                                      }}
                                    >
                                      Pay Part
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="flex-1 hover:bg-green-50 hover:text-green-600 hover:border-green-200"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarkAsPaid(purchase);
                                      }}
                                    >
                                      Mark Paid
                                    </Button>
                                  </>
                                )}
                                {purchase.payment_status === "Paid" && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="w-full text-green-600 border-green-200 bg-green-50/50"
                                    disabled
                                  >
                                    <CheckCircle2 className="w-4 h-4 mr-1.5" />{" "}
                                    Fully Paid
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
                {purchases.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No purchases found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-border/40 bg-muted/5">
                <div className="text-xs font-bold text-muted-foreground/80">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  )}{" "}
                  of {pagination.total} purchases
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchPurchases(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="h-8 text-xs font-bold border-border/80 hover:bg-muted rounded-lg"
                  >
                    <ChevronLeft className="h-3.5 w-3.5 mr-1 text-primary" />
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from(
                      { length: Math.min(5, pagination.total_pages) },
                      (_, i) => {
                        const pageNum =
                          Math.max(
                            1,
                            Math.min(
                              pagination.total_pages - 4,
                              pagination.page - 2
                            )
                          ) + i;
                        if (pageNum > pagination.total_pages) return null;
                        return (
                          <Button
                            key={pageNum}
                            variant={
                              pageNum === pagination.page
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => fetchPurchases(pageNum)}
                            className={`w-8 h-8 p-0 text-xs font-bold rounded-lg border ${
                              pageNum === pagination.page
                                ? "bg-primary text-primary-foreground shadow-md shadow-primary/10 border-primary"
                                : "border-border/80 hover:bg-muted"
                            }`}
                          >
                            {pageNum}
                          </Button>
                        );
                      }
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchPurchases(pagination.page + 1)}
                    disabled={pagination.page >= pagination.total_pages}
                    className="h-8 text-xs font-bold border-border/80 hover:bg-muted rounded-lg"
                  >
                    Next
                    <ChevronRight className="h-3.5 w-3.5 ml-1 text-primary" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {/* salt dialog  */}
      <Dialog
        open={saltDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            const idToFocus = saltDialog.itemId;
            setTimeout(
              () => document.getElementById(`add-salt-${idToFocus}`)?.focus(),
              50
            );
          }
          setSaltDialog((prev) => ({ ...prev, open }));
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Salt Composition</DialogTitle>
          </DialogHeader>

          <textarea
            value={saltDialog.value}
            onChange={(e) =>
              setSaltDialog((prev) => ({
                ...prev,
                value: e.target.value,
              }))
            }
            rows={6}
            className="w-full border border-border rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter full salt composition..."
          />

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                const idToFocus = saltDialog.itemId;
                setSaltDialog({ open: false, itemId: null, value: "" });
                setTimeout(
                  () =>
                    document.getElementById(`add-salt-${idToFocus}`)?.focus(),
                  50
                );
              }}
            >
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={() => {
                const idToFocus = saltDialog.itemId;
                handleItemFieldChange(
                  idToFocus,
                  "salt_composition",
                  saltDialog.value
                );
                setSaltDialog({ open: false, itemId: null, value: "" });
                setTimeout(
                  () =>
                    document.getElementById(`add-salt-${idToFocus}`)?.focus(),
                  50
                );
              }}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          setDeleteDialog({
            open,
            purchase: open ? deleteDialog.purchase : null,
          })
        }
      >
        <AlertDialogContent className="max-w-2xl w-[95%]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this purchase from{" "}
              {deleteDialog.purchase?.supplier_name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col-reverse md:flex-row gap-2 justify-end mt-6">
            <AlertDialogCancel className="w-full md:w-auto mt-0">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="w-full md:w-auto bg-destructive hover:bg-destructive/90 transition-colors"
              onClick={() => handleDeletePurchase(false)}
            >
              Force Delete (No Inventory Adjust)
            </AlertDialogAction>
            <AlertDialogAction
              className="w-full md:w-auto btn-primary"
              onClick={() => handleDeletePurchase(true)}
            >
              Delete & Decrease Stock
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={pdfConfirmDialog.open}
        onOpenChange={(open) =>
          setPdfConfirmDialog({ ...pdfConfirmDialog, open })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Generate Purchase PDF</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to generate and view a PDF receipt for this newly
              recorded purchase?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Skip</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                handleGeneratePurchasePdf(pdfConfirmDialog.purchaseId);
                setPdfConfirmDialog({ open: false, purchaseId: null });
              }}
              className="btn-primary"
            >
              Generate PDF
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Price History Comparison Dialog */}
      <Dialog
        open={priceHistoryDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            const idToFocus = priceHistoryDialog.itemId;
            setTimeout(
              () =>
                document.getElementById(`price-history-${idToFocus}`)?.focus(),
              50
            );
          }
          setPriceHistoryDialog({ open, itemId: null, data: null });
        }}
      >
        <DialogContent className="max-w-lg">
          {priceHistoryDialog?.data?.type === "higher" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-yellow-600">
                  <span className="bg-yellow-500 text-yellow-950 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    !
                  </span>
                  Higher Price Alert
                </DialogTitle>
                <DialogDescription>
                  You're paying more than your previous purchases for this
                  product.
                </DialogDescription>
              </DialogHeader>

              {priceHistoryDialog.data && (
                <div className="space-y-4">
                  {/* Current vs Best Price Summary */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground mb-2">
                      <div className="flex items-center gap-2">
                        <strong>
                          {priceHistoryDialog.data.searched_product_name}
                        </strong>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <strong>
                          {priceHistoryDialog.data.matched_product_name}
                        </strong>
                      </div>
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Current Rate
                        </p>
                        <p className="text-lg font-bold text-yellow-600">
                          ₹
                          {Number(
                            priceHistoryDialog?.data?.currentPrice || 0
                          ).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Best Historical Price
                        </p>
                        <p className="text-lg font-bold text-primary">
                          ₹
                          {Number(
                            priceHistoryDialog?.data?.cheapestPrice || 0
                          ).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-yellow-600 mt-2 font-medium">
                      You're paying ₹
                      {Number(
                        priceHistoryDialog?.data?.priceDifference || 0
                      ).toFixed(2)}{" "}
                      more per pack!
                    </p>
                  </div>

                  {/* Cheaper Suppliers List */}
                  <div>
                    <h4 className="text-sm font-semibold mb-2">
                      Cheaper Options (Ranked by Price)
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {priceHistoryDialog.data.cheaperOptions.map(
                        (option, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              idx === 0
                                ? "bg-primary/10 border-primary/30"
                                : "bg-muted/50 border-border"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  idx === 0
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted-foreground/20 text-muted-foreground"
                                }`}
                              >
                                {idx + 1}
                              </span>
                              <div>
                                <p className="font-medium text-sm">
                                  {option.supplier_name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {option.purchase_date?.slice(0, 10)} •{" "}
                                  {option.invoice_no || "No Invoice"}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p
                                className={`font-bold ${idx === 0 ? "text-primary" : "text-foreground"}`}
                              >
                                ₹{option.pack_price.toFixed(2)}
                              </p>
                              <p className="text-xs text-green-600">
                                Save ₹
                                {(
                                  priceHistoryDialog.data.currentPrice -
                                  option.pack_price
                                ).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() =>
                        setPriceHistoryDialog({
                          open: false,
                          itemId: null,
                          data: null,
                        })
                      }
                    >
                      Keep Current Price
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={() => {
                        // Apply the cheapest price
                        const cheapest =
                          priceHistoryDialog.data.cheaperOptions[0];
                        if (cheapest && priceHistoryDialog.itemId) {
                          handleItemFieldChangeWithCalc(
                            priceHistoryDialog.itemId,
                            "rate_pack",
                            cheapest.pack_price.toString()
                          );
                          // Clear the alert
                          setPriceAlerts((prev) => {
                            const updated = { ...prev };
                            delete updated[priceHistoryDialog.itemId];
                            return updated;
                          });
                        }
                        setPriceHistoryDialog({
                          open: false,
                          itemId: null,
                          data: null,
                        });
                        toast.success(
                          `Applied best price: ₹${cheapest.pack_price.toFixed(2)}`
                        );
                      }}
                    >
                      Apply Best Price
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
          {priceHistoryDialog?.data?.type === "cheapest" && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="font-semibold text-green-600">
                ✅ You are already at the best historical price!
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Current Rate: ₹
                {priceHistoryDialog.data.current_price.toFixed(2)}
              </p>
            </div>
          )}

          {priceHistoryDialog?.data?.type === "no-history" && (
            <div className="bg-muted/40 border border-border rounded-lg p-4">
              <p className="font-semibold text-muted-foreground">
                No past price comparison available.
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                This product has no previous purchase history yet.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={removeConfirmDialog.open}
        onOpenChange={(open) =>
          setRemoveConfirmDialog({ ...removeConfirmDialog, open })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this item from the purchase
              invoice?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveItem}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* TABS & ACTION NAVBAR */}
      {(showNewPurchase || editingPurchaseId) &&
        createPortal(
          <div className="fixed bottom-0 left-0 md:left-[250px] right-0 z-[100] flex items-center justify-between bg-card/85 backdrop-blur-xl border-t border-border/80 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] px-6 py-4 gap-4 overflow-x-auto scroller-hide overflow-y-hidden mb-0 transition-all duration-300">
            <div className="flex items-center gap-2 overflow-x-auto scroller-hide">
              {showNewPurchase &&
                tabs.map((tab, idx) => {
                  const isActive = tab.id === activeTabId;
                  let tabName = `Tab ${idx + 1}`;
                  if (tab.data?.purchaseItems?.length > 0) {
                    const firstProduct =
                      tab.data.purchaseItems[0].product_name || "Unknown";
                    const extra = tab.data.purchaseItems.length - 1;
                    tabName =
                      extra > 0 ? `${firstProduct} +${extra}` : firstProduct;
                  }
                  return (
                    <div key={tab.id} className="relative group shrink-0">
                      <Button
                        variant={isActive ? "default" : "secondary"}
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          switchTab(tab.id);
                        }}
                        className={`pr-8 h-9 rounded-full transition-all duration-200 ${isActive ? "bg-primary text-primary-foreground shadow-md font-bold ring-1 ring-primary/50" : "bg-muted/65 hover:bg-muted/90 text-muted-foreground hover:text-foreground font-medium"}`}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5 opacity-70" />
                        <span className="max-w-[120px] truncate">
                          {tabName}
                        </span>
                      </Button>
                      {tabs.length > 0 && (
                        <div
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full bg-background/50 hover:bg-destructive hover:text-destructive-foreground cursor-pointer transition-colors"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            closeTab(tab.id);
                          }}
                        >
                          <X className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                  );
                })}
              {showNewPurchase && tabs.length < 10 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={createNewTab}
                  className="bg-primary/5 hover:bg-primary/10 border-primary/20 text-primary shrink-0 transition-colors shadow-sm h-9 rounded-full"
                >
                  <Plus className="w-4 h-4 mr-1" /> New Tab
                </Button>
              )}
              {editingPurchaseId && (
                <div className="flex items-center gap-2 px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-md text-yellow-600 text-sm font-medium">
                  <Edit2 className="w-4 h-4" />
                  Editing Purchase #{editingPurchaseId}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 shrink-0 ml-auto border-l border-border pl-6">
              <Button
                variant="ghost"
                onClick={handleCancelNewPurchase}
                className="text-muted-foreground hover:text-foreground"
              >
                Cancel (Esc)
              </Button>

              <Button
                onClick={handleSubmitPurchase}
                disabled={
                  submitting ||
                  purchaseItems.filter((i) => i.product_name).length === 0
                }
                className="btn-primary shadow-lg shadow-primary/20 min-w-[160px]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    <span>
                      {editingPurchaseId ? "Update Purchase" : "Save Purchase"}
                    </span>
                    <kbd className="hidden sm:inline-block ml-1 opacity-70 text-[9px] font-mono border border-white/20 px-1 rounded">
                      {getOS() === "mac" ? "⌘Enter" : "Ctrl+Enter"}
                    </kbd>
                  </div>
                )}
              </Button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
